import React, { Component } from 'react';

import {
  RESERVATION_STATUS, RESERVATION_ORIGIN_LABEL, managedBy, businessNow, scheduleForDate,
  seatsChargeAmount, SETTLE_PAYMENTS, getPosDevices, pickDefaultPosDevice,
  seatBreakdown, addMemberSeat, removeMemberSeat, applyTableVoucher,
} from '../utils/reservationsApi';
import { membershipStatus } from '../utils/membershipsApi';
import { CHARGE_MODE_LABEL, SIGNUP_STATUS, signupPaymentChip } from '../utils/openTablesApi';
import OpenTableChargeModal from './OpenTableChargeModal';
import { navigateTo } from '../utils/navigation';
import { includedOf, hasInclusions, formatCounter, previewCorrection, menuByCodeOf, includedOriginText } from '../utils/eventIncluded';

// Vista "Salón" (Fase 1 del rediseño de Mesas): plano vivo de la cafetería.
// Presentacional + estado local del form de "sentar"; los datos (agenda, schedule)
// y las acciones (sentar, check-in, liberar, ver, lookup) llegan por props desde
// ReservationsPage, que sigue siendo dueña de la I/O.

const pad = (n) => String(n).padStart(2, '0');

// Colores por estado (el backoffice es light-only). Fondo suave + borde + texto del mismo tono.
const STATE = {
  libre:     { label: 'Libre',     bg: '#eff9f2', border: '#bfe6cd', text: '#1c7a48', dot: '#2fa365' },
  mesa:      { label: 'En mesa',   bg: '#eaf2fc', border: '#bcd8f5', text: '#1a5fa5', dot: '#2f7fd1' },
  reservada: { label: 'Reservada', bg: '#fdf4e3', border: '#f1dba8', text: '#8a6212', dot: '#d99a2b' },
  bloqueada: { label: 'Bloqueada', bg: '#f2f2f2', border: '#dddddd', text: '#777777', dot: '#999999' },
  // FR-050: la mesa abierta se distingue de la reserva común — vende sus lugares de a uno
  // y lo que importa es el M/N de anotados. Violeta: mismo esquema fondo/borde/texto/punto.
  abierta:   { label: 'Mesa abierta', bg: '#f5f0fc', border: '#d9c9f1', text: '#5b3a9e', dot: '#7e57c2' },
  // FR-073: la mesa que tomó una jornada del club. Ni reserva común (no la creó ni la libera
  // el salón) ni mesa abierta (el violeta ya es de ella): rosa frambuesa propio —el verde-azulado
  // de antes se confundía con Libre—, y las N mesas de la misma jornada se leen con el MISMO
  // nombre y el MISMO color.
  actividad: { label: 'Jornada',      bg: '#fcedf3', border: '#f2c4d6', text: '#9b2257', dot: '#d6417f' },
};

// Hueco mínimo para que valga la pena sentar gente antes de una reserva: con menos de esto
// no da para jugar, así que la mesa se muestra como Reservada.
const MIN_USABLE_MINUTES = 45;

const SEAT_PAYMENTS = [
  ['efectivo', 'Efectivo'],
  ['pos', 'POS'],
  ['transferencia', 'Transferencia'],
  ['mercadopago', 'MercadoPago'],
];

const emptySeatForm = () => ({
  playerCount: 2,
  paymentMethod: 'pos', // POS: es el medio más usado en el salón
  cedula: '',
  name: '',
  accountId: null,
  gameCode: '',
  deferCover: false, // por defecto el cover se cobra al sentar
  // Socios con lugar sin cargo (cédulas ya validadas): cada uno suma una persona gratis.
  // `playerCount` pasa a ser "los que pagan".
  members: [], // [{ cedula, name, plan }]
  memberCedula: '',
});

// "¿Este socio tiene mesa sin cargo hoy?" a partir del estado de su membresía.
function memberHint(status) {
  if (!status || !status.active) return { ok: false, text: 'No tiene una membresía vigente.' };
  const plan = (status.current && (status.current.plan_name || (status.current.plan && status.current.plan.name))) || 'su plan';
  if (status.benefits && status.benefits.freeTableToday) return { ok: true, text: `Socio · ${plan}`, plan };
  return { ok: false, text: `Su plan (${plan}) no incluye mesa sin entrada hoy.` };
}

class SalonView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTableId: null,
      seat: emptySeatForm(),
      seatBusy: false,
      seatError: null,
      clientHint: null,
      seatResult: null, // resultado MP (link de pago) cuando el cover se paga con MercadoPago
      // F1 del arqueo: los lectores de tarjeta, para saber por cuál entró el cobro.
      posDevices: [],
      posDeviceId: '',
    };
  }

  componentDidMount() {
    this.loadPosDevices();
  }

  loadPosDevices = async () => {
    try {
      const devices = await getPosDevices();
      const preferido = pickDefaultPosDevice(devices);
      if (preferido) this.setState({ posDevices: devices, posDeviceId: String(preferido.id) });
    } catch (e) { /* sin lectores el cobro sigue funcionando igual */ }
  }

  // --- Config del salón (con fallback a defaults del backend) ---
  sch = () => {
    // Horario de la jornada en curso: puede tener override por día (viernes/sábados hasta 24).
    const s = scheduleForDate(this.props.schedule, businessNow(this.props.schedule).date) || {};
    return {
      openHour: s.openHour != null ? Number(s.openHour) : 14,
      lastReservation: s.lastReservation != null ? Number(s.lastReservation) : 21,
      pricePerPlayer: s.pricePerPlayer != null ? Number(s.pricePerPlayer) : 250,
      minPlayers: s.minPlayers != null ? Number(s.minPlayers) : 1,
      maxPlayers: s.maxPlayers != null ? Number(s.maxPlayers) : 8,
    };
  };

  // --- Estado de cada mesa "ahora" a partir de la agenda del día ---
  tableModel = (table) => {
    const { reservations = [], blocks = [] } = this.props.agenda || {};
    const now = businessNow(this.props.schedule);
    const nowHour = now.hour;
    const res = reservations.filter(
      (r) => r.table_id === table.id && r.status !== 'cancelled' && r.status !== 'completed'
    );
    const seated = res.find((r) => r.status === 'seated');
    const blocked = blocks.some(
      (b) => (b.table_id == null || b.table_id === table.id) && b.start_hour <= nowHour && b.end_hour > nowHour
    );
    const upcoming = res
      .filter((r) => r.status === 'active' || r.status === 'pending')
      .sort((a, b) => a.start_hour - b.start_hour)[0];

    if (seated) return { state: 'mesa', seated, upcoming };
    if (blocked) return { state: 'bloqueada' };
    // Una reserva sólo ocupa la mesa DESDE su hora: una reserva a las 20 no puede dejar la mesa
    // muerta desde las 14. Pero el hueco previo sólo sirve si alcanza para jugar: se mide la
    // VENTANA LIBRE real —desde ahora (o desde que abre, si todavía no abrió) hasta la reserva—
    // y si es menor a MIN_USABLE_MINUTES la mesa ya se muestra ocupada. Esto también cubre el
    // caso "reserva a la hora de apertura": ahí la ventana es 0.
    // FR-003/FR-050: la reserva abierta (viene con openTable{} en la agenda) entra en la MISMA
    // regla de ventana — ocupa desde su hora, no desde la apertura—, sólo que cuando ocupa se
    // pinta con su estado propio ('abierta', contador de anotados) en vez de 'Reservada'. Y como
    // compite como `upcoming` por hora, una reserva común más temprana en la misma mesa vuelve a
    // verse (antes el find de la abierta la tapaba todo el día). Sentada (primer check-in) sigue
    // el flujo normal de 'mesa' de arriba.
    if (upcoming) {
      // FR-073 — la mesa que tomó una jornada del club sale del pool del salón, y se lee así
      // DESDE QUE LA AGENDA LA TRAE, no recién 45 minutos antes de la hora.
      //
      // La decisión tiene costo y se toma a ojos abiertos: una jornada de las 20 deja sus N
      // mesas sin vender toda la tarde. A cambio, (a) el mostrador no sienta un walk-in en una
      // mesa que a las 20 tiene que estar libre —el backend tolera ese caso dejando la hija sin
      // sentar y avisando (FR-060), pero la noche arranca con una mesa menos y gente repartida
      // de más—, y (b) es la ÚNICA puerta al panel de la hija (FR-074: "Sentar la jornada",
      // "Ir a la jornada", las hermanas) antes de la hora: dentro de la regla de ventana, a las
      // 19:00 la mesa todavía se vería libre y el panel sería el form de walk-in.
      //
      // Por PRESENCIA del adjunto `activity`, nunca por `origin` (mismo criterio que
      // `openTable`): el adjunto es best-effort en el backend, así que una hija sin él se lee
      // como la reserva común que ya se veía ayer — degrada, no rompe.
      if (upcoming.activity) return { state: 'actividad', act: upcoming };
      const openHour = this.sch().openHour;
      const nowDecimal = nowHour + (Number(now.minute) || 0) / 60;
      const windowStart = Math.max(nowDecimal, openHour);
      const freeMinutes = (upcoming.start_hour - windowStart) * 60;
      if (freeMinutes < MIN_USABLE_MINUTES) {
        if (upcoming.openTable) return { state: 'abierta', open: upcoming };
        return { state: 'reservada', upcoming };
      }
      return { state: 'libre', upcoming, freeMinutes };
    }
    return { state: 'libre' };
  };

  selectTable = (id) => {
    // Se cierran también los mini-forms abiertos: si no, el de la mesa anterior queda montado
    // sobre la nueva y el próximo click actúa sobre la reserva equivocada.
    this.setState({
      selectedTableId: id, seat: emptySeatForm(), seatError: null, clientHint: null, seatResult: null,
      accountFor: null, account: null,
      seatsError: null, addingGameFor: null,
      checkingIn: null, checkInError: null, movingFrom: null, moveError: null,
      seatingActivity: null, activityError: null, activityCount: 0,
      memberForm: null,
      voucherForm: null, // voucher de mesa en curso (premio o cortesía «Mesa para N»)
    });
  };

  // --- Sentar gente (walk-in "para ahora") ---
  setSeat = (patch) => this.setState({ seat: { ...this.state.seat, ...patch } });

  stepPlayers = (delta) => {
    const { minPlayers, maxPlayers } = this.sch();
    // Los socios cuentan para el mínimo y el máximo de la mesa: con dos socios se puede sentar
    // a "0 que pagan".
    const socios = this.state.seat.members.length;
    const next = Math.max(Math.max(0, minPlayers - socios), Math.min(maxPlayers - socios, Number(this.state.seat.playerCount) + delta));
    this.setSeat({ playerCount: next });
  };

  // Agregar una cédula socia al form de sentar: se valida contra su membresía ANTES de sentar,
  // así el rechazo (plan sin mesa ese día, no es socio) se ve acá y no después del cobro.
  addSeatMember = async () => {
    const seat = this.state.seat;
    const cedula = String(seat.memberCedula || '').replace(/\D/g, '');
    if (!cedula) return;
    if (seat.members.some((m) => m.cedula === cedula)) { this.setState({ seatError: 'Esa cédula ya está en la lista de socios.' }); return; }
    this.setState({ seatError: null });
    try {
      // Contra la JORNADA (a las 00:30 sigue siendo la noche anterior), que es lo que valida el backend.
      const status = await membershipStatus(cedula, businessNow(this.props.schedule).date);
      const hint = memberHint(status);
      if (!hint.ok) { this.setState({ seatError: `${cedula}: ${hint.text} Sumalo como persona que paga.` }); return; }
      const name = status.current && status.current.user ? `${status.current.user.firstName || ''} ${status.current.user.lastName || ''}`.trim() : '';
      // El socio SUMA una persona gratis (decisión del dueño): "Personas" sigue siendo los que pagan.
      // Si la mesa ya estaba en el máximo, los que pagan bajan para que el total entre en el rango.
      const { maxPlayers } = this.sch();
      const members = [...seat.members, { cedula, name, plan: hint.plan }];
      this.setSeat({ members, memberCedula: '', playerCount: Math.min(Number(seat.playerCount), Math.max(0, maxPlayers - members.length)) });
    } catch (e) {
      this.setState({ seatError: e.message || 'No se pudo consultar la membresía' });
    }
  };

  removeSeatMember = (cedula) => {
    const seat = this.state.seat;
    // Si quitarlo deja la mesa por debajo del mínimo, los que pagan suben al mínimo.
    const members = seat.members.filter((m) => m.cedula !== cedula);
    const { minPlayers } = this.sch();
    this.setSeat({ members, playerCount: Math.max(Number(seat.playerCount), minPlayers - members.length) });
  };

  lookupClient = async () => {
    const cedula = (this.state.seat.cedula || '').trim();
    if (!cedula || !this.props.lookupClient) { this.setState({ clientHint: null }); return; }
    const found = await this.props.lookupClient(cedula);
    if (found && (found.name || found.accountId)) {
      this.setSeat({ name: found.name || this.state.seat.name, accountId: found.accountId || null });
      this.setState({ clientHint: { type: 'ok', text: found.name ? `✓ ${found.name}` : '✓ Cliente encontrado' } });
    } else {
      this.setState({ clientHint: { type: 'new', text: 'Cliente nuevo' } });
    }
  };

  submitSeat = async (table) => {
    if (this.state.seatBusy) return;
    // "Sentar" = la gente YA está en la mesa. Se manda la intención (`seatNow`) y la fecha/hora
    // las pone el backend con su propio reloj de jornada: así queda 'seated' a la hora real,
    // sin depender del reloj del navegador ni del horario de apertura.
    const seat = this.state.seat;
    const payload = {
      seatNow: true,
      playerCount: Number(seat.playerCount),
      paymentMethod: seat.paymentMethod,
      gameCode: seat.gameCode || null,
      tableId: table.id,
      memberCedulas: seat.members.map((m) => m.cedula),
      customer: (seat.name || seat.cedula)
        ? { name: seat.name || null, cedula: seat.cedula || null, accountId: seat.accountId }
        : null,
      guests: [],
      // Si se difiere, el cover queda impago en la cuenta de la mesa y se cobra después.
      deferCover: !!seat.deferCover,
      // Cobrada en el acto con POS (el default del salón): por cuál lector entró. Sin esto la
      // entrada quedaba en la caja pero fuera del lote de cada aparato (spec-caja D-03).
      ...(!seat.deferCover && seat.paymentMethod === 'pos' && this.state.posDeviceId
        ? { posDeviceId: Number(this.state.posDeviceId) } : {}),
    };
    this.setState({ seatBusy: true, seatError: null });
    try {
      const result = await this.props.onSeat(payload);
      const mpLink = result && result.preference && result.preference.init_point;
      if (mpLink) {
        // MercadoPago: la mesa queda pendiente de pago; mostrar el link/QR para cobrar.
        this.setState({ seatBusy: false, seatResult: { mpLink, label: table.label }, seat: emptySeatForm(), clientHint: null });
      } else {
        this.setState({ seatBusy: false, seat: emptySeatForm(), clientHint: null });
      }
    } catch (e) {
      this.setState({ seatBusy: false, seatError: e.message || 'No se pudo sentar a la mesa' });
    }
  };

  // ------------------------------------------------------------------ KPIs
  renderKpis(tables) {
    const models = tables.map((t) => this.tableModel(t));
    const enMesa = models.filter((m) => m.state === 'mesa');
    const libres = models.filter((m) => m.state === 'libre').length;
    const personas = enMesa.reduce((acc, m) => acc + (Number(m.seated.player_count) || 0), 0);
    const nowHour = businessNow(this.props.schedule).hour;
    const proximas = (this.props.agenda.reservations || [])
      .filter((r) => (r.status === 'active' || r.status === 'pending') && r.start_hour >= nowHour)
      .sort((a, b) => a.start_hour - b.start_hour);
    const prox = proximas.length ? `${pad(proximas[0].start_hour)}:00` : '—';

    const kpi = (label, value) => (
      <div className="col text-center" style={{ minWidth: 90 }}>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 500 }}>{value}</div>
      </div>
    );
    return (
      <div className="row no-gutters mb-3" style={{ background: '#fafafa', borderRadius: 8, padding: '10px 0' }}>
        {kpi('En mesa', enMesa.length)}
        {kpi('Personas', personas)}
        {kpi('Libres', libres)}
        {kpi('Próx. llegada', prox)}
      </div>
    );
  }

  renderLegend() {
    const item = (k) => (
      <span key={k} className="mr-3 d-inline-flex align-items-center" style={{ fontSize: 12, color: '#666' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATE[k].dot, display: 'inline-block', marginRight: 5 }} />
        {STATE[k].label}
      </span>
    );
    return (
      <div className="mb-3">
        {['libre', 'mesa', 'reservada', 'abierta', 'actividad', 'bloqueada'].map(item)}
        {/* Sub-estado: verde con chip ámbar = se puede usar, pero hay una reserva más tarde. */}
        <span className="d-inline-flex align-items-center" style={{ fontSize: 12, color: '#666' }}>
          <span style={{
            background: STATE.reservada.bg, color: STATE.reservada.text,
            border: `1px solid ${STATE.reservada.border}`, borderRadius: 10,
            padding: '0 6px', fontSize: 11, marginRight: 5,
          }}>hasta HH</span>
          Libre con reserva más tarde
        </span>
      </div>
    );
  }

  /**
   * FR-073/FR-074 — "Mesa k de N" de una hija. `tableIds` llega ORDENADO del backend
   * (`GROUP_CONCAT … ORDER BY table_id`), así que la posición es la misma mirada desde
   * cualquier mesa: 6-A dice "mesa 1 de 3" y 6-B "mesa 2 de 3" aunque se hayan sumado al revés.
   */
  activitySeat = (r, tableId) => {
    const act = (r && r.activity) || {};
    const ids = Array.isArray(act.tableIds) ? act.tableIds : [];
    const k = ids.findIndex((id) => Number(id) === Number(tableId)) + 1;
    const n = Number(act.tableCount) || ids.length || 0;
    if (k > 0 && n > 0) return { k, n, label: `Mesa ${k} de ${n}` };
    return { k: 0, n, label: n > 0 ? `${n} mesa${n > 1 ? 's' : ''} en la jornada` : null };
  };

  /**
   * Las HERMANAS de una hija, con su label del salón y si ya están sentadas. Se resuelven
   * contra la agenda (el adjunto sólo trae los `tableIds`): una mesa que no esté en la agenda
   * de hoy igual se nombra por su id antes que desaparecer de la lista.
   */
  activitySiblings = (r, tableId) => {
    const act = (r && r.activity) || {};
    const tables = this.props.agenda.tables || [];
    const reservations = this.props.agenda.reservations || [];
    return (Array.isArray(act.tableIds) ? act.tableIds : [])
      .filter((id) => Number(id) !== Number(tableId))
      .map((id) => {
        const t = tables.find((x) => Number(x.id) === Number(id));
        const child = reservations.find((x) => Number(x.table_id) === Number(id)
          && Number(x.activity_id) === Number(act.id)
          && x.status !== 'cancelled' && x.status !== 'completed');
        return {
          id,
          label: (t && t.label) || `#${id}`,
          seated: !!(child && child.status === 'seated'),
          players: child ? Number(child.player_count) || 0 : null,
        };
      });
  };

  // ------------------------------------------------------------------ Floor
  renderCard(table) {
    const m = this.tableModel(table);
    // Pedido del dueño (For Test): la mesa abierta conserva el violeta también sentada — es la
    // mesa social del salón y se distingue de un vistazo. Cambia sólo la identidad visual de la
    // tarjeta: el panel y las acciones siguen siendo los de una mesa 'en mesa'.
    const abiertaSentada = m.state === 'mesa' && m.seated && m.seated.openTable;
    // FR-073: lo mismo con la hija sentada — las N mesas de la jornada tienen que seguir
    // leyéndose como un solo grupo cuando la gente ya está adentro.
    const actividadSentada = m.state === 'mesa' && m.seated && m.seated.activity;
    const st = abiertaSentada ? STATE.abierta : (actividadSentada ? STATE.actividad : STATE[m.state]);
    const selected = this.state.selectedTableId === table.id;
    let sub = null;
    // Libre CON tope: se puede usar, pero hay una reserva más tarde. Se destaca con un chip
    // ámbar (y borde ámbar abajo) para que no se pierda entre las verdes.
    const cappedUntil = m.state === 'libre' && m.upcoming ? m.upcoming : null;
    if (m.state === 'libre') sub = cappedUntil ? (
      <div>
        <span style={{
          display: 'inline-block', background: STATE.reservada.bg, color: STATE.reservada.text,
          border: `1px solid ${STATE.reservada.border}`, borderRadius: 10,
          padding: '1px 8px', fontSize: 12, fontWeight: 500,
        }}>
          hasta {pad(cappedUntil.start_hour)}:00
        </span>
        <div style={{ fontSize: 11, color: STATE.reservada.text, marginTop: 3 }}>
          {/* FR-050: la próxima puede ser una mesa abierta (sin nombre): decir qué es. */}
          {cappedUntil.openTable ? 'mesa abierta' : 'reservada'} {pad(cappedUntil.start_hour)}:00{cappedUntil.name ? ` · ${cappedUntil.name}` : ''}
        </div>
      </div>
    ) : (
      <div style={{ fontSize: 12, color: st.text }}>Tocá para sentar</div>
    );
    if (m.state === 'mesa') {
      // Juegos de la sesión (Fase 2); si todavía no cargaron ninguno, cae al juego de la reserva.
      const games = (m.seated.games || []).map((g) => g.game_name);
      const gameLabel = games.length ? games.join(', ') : (m.seated.game_name || '');
      sub = (
        <div style={{ fontSize: 12, color: st.text }}>
          {m.seated.player_count} pers · desde {pad(m.seated.start_hour)}:00
          {gameLabel ? ` · ${gameLabel}` : ''}
        </div>
      );
    }
    if (m.state === 'reservada') sub = (
      <div style={{ fontSize: 12, color: st.text }}>
        {pad(m.upcoming.start_hour)}:00 · {m.upcoming.player_count} pers{m.upcoming.name ? ` · ${m.upcoming.name}` : ''}
      </div>
    );
    // FR-050: el dato de la mesa abierta es cuántos anotados van llegando sobre el cupo.
    if (m.state === 'abierta') {
      const ot = m.open.openTable;
      sub = (
        <div style={{ fontSize: 12, color: st.text }}>
          {pad(m.open.start_hour)}:00 · {ot.confirmed}/{ot.cupo} anotados
          {ot.hostName ? ` · de ${ot.hostName}` : ''}
          {m.open.game_name ? ` · ${m.open.game_name}` : ''}
        </div>
      );
    }
    // FR-073: la hija todavía sin sentar. El nombre de la jornada es el dato (se repite en las
    // N mesas) y "mesa k de N" dice de un vistazo cuántas más hay que mirar.
    if (m.state === 'actividad') {
      const { label: seatLabel } = this.activitySeat(m.act, table.id);
      sub = (
        <div style={{ fontSize: 12, color: st.text }}>
          {pad(m.act.start_hour)}:00 · {m.act.activity.name || m.act.name || 'Jornada del club'}
          {seatLabel ? ` · ${seatLabel.toLowerCase()}` : ''}
        </div>
      );
    }
    if (m.state === 'bloqueada') sub = <div style={{ fontSize: 12, color: st.text }}>No disponible</div>;

    return (
      <div
        key={table.id}
        onClick={() => this.selectTable(table.id)}
        style={{
          background: st.bg,
          // Borde ámbar cuando está libre pero con reserva encima: verde = usable, ámbar = con tope.
          border: `1px solid ${cappedUntil ? STATE.reservada.border : st.border}`,
          outline: selected ? '2px solid #2f7fd1' : 'none',
          outlineOffset: 2,
          borderRadius: 12,
          padding: 12,
          cursor: 'pointer',
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <span style={{ fontWeight: 500, color: st.text }}>{table.label}</span>
          {table.is_vip
            ? <span style={{ color: st.text }} title="VIP">★</span>
            : <span style={{ fontSize: 11, color: st.text }}>{table.capacity} p</span>}
        </div>
        <div style={{ fontSize: 11, color: st.text, margin: '4px 0 6px' }}>
          {abiertaSentada && 'Mesa abierta · en mesa'}
          {actividadSentada && `${m.seated.activity.name || m.seated.name || 'Jornada'} · en mesa`}
          {!abiertaSentada && !actividadSentada && st.label}
        </div>
        {sub}
      </div>
    );
  }

  // ------------------------------------------------------------------ Panel
  renderPanel() {
    const { selectedTableId, seatResult, accountFor } = this.state;
    if (accountFor) return this.renderAccountPanel();
    if (seatResult) return this.renderSeatResult();
    const tables = this.props.agenda.tables || [];
    const table = tables.find((t) => t.id === selectedTableId);
    if (!table) {
      return (
        <div className="text-muted text-center" style={{ paddingTop: 70, fontSize: 14 }}>
          Tocá una mesa para sentar gente<br />o gestionar la mesa
        </div>
      );
    }
    const m = this.tableModel(table);
    if (m.state === 'libre') return this.renderSeatForm(table, m.upcoming);
    if (m.state === 'mesa') return this.renderOccupiedPanel(table, m.seated, m.upcoming);
    if (m.state === 'reservada') return this.renderReservedPanel(table, m.upcoming);
    if (m.state === 'abierta') return this.renderOpenTablePanel(table, m.open);
    if (m.state === 'actividad') return this.renderActivityPanel(table, m.act);
    return (
      <div>
        <div style={{ fontWeight: 500, fontSize: 16 }}>{table.label}</div>
        <div className="text-muted" style={{ fontSize: 13, marginTop: 6 }}>Mesa bloqueada — no disponible ahora.</div>
      </div>
    );
  }

  row(label, value) {
    return (
      <div className="d-flex justify-content-between" style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
        <span className="text-muted">{label}</span><span style={{ fontWeight: 500 }}>{value}</span>
      </div>
    );
  }

  renderSeatResult() {
    const { mpLink, label } = this.state.seatResult;
    return (
      <div>
        <div style={{ fontWeight: 500, fontSize: 16 }}>{label}</div>
        <div style={{ fontSize: 12, color: STATE.reservada.text, marginBottom: 12 }}>Pendiente de pago — MercadoPago</div>
        <p className="text-muted" style={{ fontSize: 13 }}>La mesa se activa cuando se confirma el pago. Mostrale el link/QR al cliente:</p>
        <a className="btn btn-accent btn-block btn-sm" href={mpLink} target="_blank" rel="noreferrer">Abrir pago MercadoPago</a>
        <button type="button" className="btn btn-white btn-block btn-sm mt-2" onClick={() => this.setState({ seatResult: null, selectedTableId: null })}>Listo</button>
      </div>
    );
  }

  renderSeatForm(table, upcoming) {
    const { pricePerPlayer } = this.sch();
    const { seat, seatBusy, seatError, clientHint } = this.state;
    const total = Number(seat.playerCount) * pricePerPlayer;
    const socios = seat.members.length;
    // La mesa abierta queda afuera: su gente entra por inscripción, no por un check-in de la
    // reserva que la sostiene (ese camino tiene su propio panel).
    const upcomingArrival = upcoming && !upcoming.openTable;
    const upcomingPaid = upcomingArrival && seatBreakdown(upcoming).paid > 0;

    // Validando la llegada de la reserva, el form de walk-in se va: son dos cosas distintas
    // ("cuántos vinieron" vs. "a cuántos sentás") y con los dos steppers en pantalla no se sabe
    // cuál se está tocando. Cancelar devuelve el form.
    if (upcomingArrival && this.state.checkingIn === upcoming.id) {
      return (
        <div>
          <div style={{ fontWeight: 500, fontSize: 16 }}>{upcoming.name || 'Reserva'}</div>
          <div style={{ fontSize: 12, color: STATE.reservada.text }}>
            {table.label} · reservada {pad(upcoming.start_hour)}:00 · {upcoming.player_count} personas
          </div>
          {this.renderArrivalForm(upcoming)}
        </div>
      );
    }
    return (
      <div>
        <div style={{ fontWeight: 500, fontSize: 16 }}>Sentar en {table.label}</div>
        <div style={{ fontSize: 12, color: STATE.libre.text, marginBottom: upcoming ? 6 : 14 }}>Mesa libre · {table.capacity} personas</div>
        {/* La mesa tiene una reserva más tarde: se puede usar igual, avisando hasta cuándo. */}
        {upcoming && (
          <div className="alert alert-warning py-2" style={{ fontSize: 12, marginBottom: 12 }}>
            {upcoming.openTable ? 'Mesa abierta' : 'Reservada'} a las {pad(upcoming.start_hour)}:00{upcoming.name ? ` (${upcoming.name})` : ''} — avisale al cliente que la mesa es hasta esa hora.
            {/* Si las entradas de esa reserva ya están cobradas, decirlo ACÁ: este form cobra la
                entrada de quien se siente, y sin el aviso el mostrador no tiene cómo saber que
                la gente que tiene enfrente quizá ya pagó. */}
            {upcomingPaid && ` ${upcoming.name || 'Esa reserva'} ya pagó sus ${seatBreakdown(upcoming).paid} entradas.`}
          </div>
        )}

        {/* El cliente DE LA RESERVA que llega temprano. Mientras falte más de MIN_USABLE_MINUTES
            la mesa figura libre, así que la única puerta que ofrecía el salón era este form de
            walk-in — y sentarlo por ahí le cobra la entrada de nuevo y deja la reserva sin sentar.
            Antes daba igual (ninguna reserva futura podía estar paga); desde que el pago se puede
            registrar por adelantado, es un cobro doble a la vista. */}
        {upcomingArrival && (
          <button type="button" className="btn btn-white btn-block mb-3" style={{ fontSize: 13 }}
            onClick={() => this.setState({ checkingIn: upcoming.id, checkInCount: Number(upcoming.player_count) || 1, checkInError: null })}>
            ¿Llegó {upcoming.name || 'la reserva'}? Validar llegada
          </button>
        )}

        <label className="d-block mb-1"><small>{socios ? 'Personas que pagan' : 'Personas'}</small></label>
        <div className="d-flex align-items-center mb-1">
          <button type="button" className="btn btn-sm btn-white" onClick={() => this.stepPlayers(-1)}>–</button>
          <span style={{ fontSize: 22, fontWeight: 500, minWidth: 34, textAlign: 'center' }}>{seat.playerCount}</span>
          <button type="button" className="btn btn-sm btn-white" onClick={() => this.stepPlayers(1)}>+</button>
          {socios > 0 && <span className="text-muted ml-2" style={{ fontSize: 12 }}>+ {socios} socio{socios > 1 ? 's' : ''} sin cargo</span>}
        </div>
        {(Number(seat.playerCount) + socios) > table.capacity && <div style={{ fontSize: 11, color: '#b5851a', marginBottom: 8 }}>Supera la capacidad de la mesa ({table.capacity}).</div>}

        {/* Socios con mesa sin cargo: cada cédula suma una persona gratis. */}
        <label className="d-block mb-1 mt-2"><small>Socios (mesa sin cargo)</small></label>
        {seat.members.map((m) => (
          <div key={m.cedula} className="d-flex justify-content-between align-items-center" style={{ fontSize: 12, padding: '2px 0' }}>
            <span><span className="text-success">✓</span> {m.name || m.cedula} <span className="text-muted">· {m.plan}</span></span>
            <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => this.removeSeatMember(m.cedula)}>Quitar</button>
          </div>
        ))}
        <div className="d-flex mb-2" style={{ gap: 6 }}>
          <input type="text" className="form-control form-control-sm" placeholder="Cédula del socio"
            value={seat.memberCedula}
            onChange={(e) => this.setSeat({ memberCedula: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') this.addSeatMember(); }} />
          <button type="button" className="btn btn-sm btn-white text-nowrap" disabled={!String(seat.memberCedula || '').trim()} onClick={this.addSeatMember}>+ Socio</button>
        </div>

        <label className="d-block mb-1 mt-2"><small>Cobro de la entrada</small></label>
        <select className="form-control form-control-sm mb-1" value={seat.deferCover ? 'later' : 'now'}
          onChange={(e) => this.setSeat({ deferCover: e.target.value === 'later' })}>
          <option value="now">Cobrar ahora</option>
          <option value="later">Cargar a la cuenta (se cobra después)</option>
        </select>
        {!seat.deferCover && (
          <select className="form-control form-control-sm mb-2" value={seat.paymentMethod}
            onChange={(e) => this.setSeat({ paymentMethod: e.target.value })}>
            {SEAT_PAYMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        {/* El mismo lector que el cobro de la cuenta: es la elección del salón, no de cada cobro. */}
        {!seat.deferCover && seat.paymentMethod === 'pos' && this.state.posDevices.length > 1 && (
          <select className="form-control form-control-sm mb-2" value={this.state.posDeviceId}
            aria-label="Lector de tarjeta" onChange={(e) => this.setState({ posDeviceId: e.target.value })}>
            {this.state.posDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}

        <div className="form-row">
          <div className="form-group col-5 mb-2">
            <label className="mb-1"><small>Cédula (opc.)</small></label>
            <input type="text" className="form-control form-control-sm" placeholder="Cédula"
              value={seat.cedula}
              onChange={(e) => { this.setSeat({ cedula: e.target.value, accountId: null }); this.setState({ clientHint: null }); }}
              onBlur={this.lookupClient} />
            {clientHint && <small className={clientHint.type === 'ok' ? 'text-success' : 'text-muted'}>{clientHint.text}</small>}
          </div>
          <div className="form-group col-7 mb-2">
            <label className="mb-1"><small>Nombre (opc.)</small></label>
            <input type="text" className="form-control form-control-sm" placeholder="Nombre"
              value={seat.name} onChange={(e) => this.setSeat({ name: e.target.value })} />
          </div>
        </div>

        {(this.props.demoGames || []).length > 0 && (
          <div className="mb-2">
            <label className="d-block mb-1"><small>Juego demo (opc.)</small></label>
            <select className="form-control form-control-sm" value={seat.gameCode}
              onChange={(e) => this.setSeat({ gameCode: e.target.value })}>
              <option value="">Sin juego</option>
              {this.props.demoGames.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
            </select>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-baseline" style={{ borderTop: '0.5px solid #eee', paddingTop: 10, marginTop: 6 }}>
          <span className="text-muted" style={{ fontSize: 13 }}>Entradas</span>
          <span style={{ fontWeight: 500 }}>${total} <small className="text-muted">({seat.playerCount} × ${pricePerPlayer}{socios ? ` + ${socios} socio${socios > 1 ? 's' : ''}` : ''})</small></span>
        </div>

        {seatError && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: 13 }}>{seatError}</div>}

        {(() => {
          // Total fuera del rango del salón (socios incluidos): avisar acá y no dejar sentar.
          const { minPlayers, maxPlayers } = this.sch();
          const totalPeople = Number(seat.playerCount) + socios;
          return totalPeople < minPlayers || totalPeople > maxPlayers ? (
            <div className="text-danger mt-2" style={{ fontSize: 12 }}>La mesa tiene que tener entre {minPlayers} y {maxPlayers} personas (socios incluidos).</div>
          ) : null;
        })()}
        <button type="button" className="btn btn-accent btn-block btn-sm mt-3"
          disabled={seatBusy || (Number(seat.playerCount) + socios) < this.sch().minPlayers || (Number(seat.playerCount) + socios) > this.sch().maxPlayers}
          onClick={() => this.submitSeat(table)}>
          {seatBusy ? '...' : 'Sentar y cobrar entrada'}
        </button>
      </div>
    );
  }

  // --- Juegos de la sesión (Fase 2): la mesa va sumando juegos mientras juega ---
  submitGame = async (reservationId) => {
    const name = (this.state.newGame || '').trim();
    if (!name || this.state.gameBusy) return;
    // Si el nombre coincide con un juego del catálogo demo, mandar también su código para
    // que quede vinculado; si no, va como nombre libre.
    const match = (this.props.demoGames || []).find(
      (g) => String(g.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    this.setState({ gameBusy: true });
    try {
      await this.props.onAddGame(reservationId, match ? { gameCode: match.code } : { gameName: name });
      this.setState({ gameBusy: false, newGame: '' });
    } catch (e) {
      this.setState({ gameBusy: false, seatError: e.message || 'No se pudo agregar el juego' });
    }
  };

  removeGame = async (reservationId, gameId) => {
    if (this.state.gameBusy) return;
    this.setState({ gameBusy: true });
    try {
      await this.props.onRemoveGame(reservationId, gameId);
      this.setState({ gameBusy: false });
    } catch (e) {
      this.setState({ gameBusy: false, seatError: e.message || 'No se pudo quitar el juego' });
    }
  };

  // Los juegos pasan a ser UNA FILA más de la ficha (como Cliente o Consumos), con el alta
  // plegada detrás de un `+`. Antes eran un bloque con título, lista e input siempre abiertos,
  // metido entre los datos y los botones: cortaba la lectura de la ficha para algo que se toca
  // dos veces por mesa.
  renderGamesRow(seated) {
    const games = seated.games || [];
    const open = this.state.addingGameFor === seated.id;
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
          <span className="text-muted">Juegos</span>
          <span style={{ textAlign: 'right' }}>
            {games.map((g) => (
              <span key={g.id} className="d-inline-flex align-items-center" style={{ marginLeft: 6 }}>
                <span style={{ fontWeight: 500 }}>{g.game_name}</span>
                <button type="button" className="btn btn-sm btn-white" title="Quitar juego"
                  style={{ lineHeight: 1, padding: '0 4px', marginLeft: 3, fontSize: 11 }}
                  disabled={this.state.gameBusy} onClick={() => this.removeGame(seated.id, g.id)}>✕</button>
              </span>
            ))}
            {games.length === 0 && <span className="text-muted" style={{ marginRight: 2 }}>—</span>}
            <button type="button" className="btn btn-sm btn-white" title="Agregar juego"
              style={{ lineHeight: 1, padding: '0 7px', marginLeft: 6 }}
              onClick={() => this.setState({ addingGameFor: open ? null : seated.id })}>{open ? '✕' : '+'}</button>
          </span>
        </div>
        {open && (
          // Combo con filtrado incremental sobre los juegos demo: se escribe y el navegador va
          // acotando la lista. Igual acepta un nombre libre (juego fuera del catálogo).
          <div className="d-flex mt-2">
            <input type="text" className="form-control form-control-sm mr-1" placeholder="Agregar juego"
              list="salon-demo-games" autoComplete="off" autoFocus
              value={this.state.newGame || ''}
              onChange={(e) => this.setState({ newGame: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') this.submitGame(seated.id); }} />
            <datalist id="salon-demo-games">
              {(this.props.demoGames || []).map((g) => <option key={g.code} value={g.name} />)}
            </datalist>
            <button type="button" className="btn btn-sm btn-white" disabled={this.state.gameBusy}
              onClick={() => this.submitGame(seated.id)}>Agregar</button>
          </div>
        )}
      </div>
    );
  }

  // --- Cuenta de la mesa (Fase 3): cobros parciales eligiendo qué se paga ---
  // ENI-197: importa CON QUÉ INTENCIÓN se abrió la cuenta. Desde "Cobrar $X para poder
  // liberar" el objetivo real es liberar la mesa, así que si el cobro deja saldo cero se
  // encadena el liberado en la misma acción. Desde "Ver / cobrar cuenta" no: ahí el cajero
  // puede estar cobrando parcial y la mesa sigue ocupada.
  openAccount = async (reservationId, { closing = false } = {}) => {
    this.setState({ accountFor: reservationId, accountClosing: closing, account: null, accountBusy: false, payError: null, paySeats: 0, payOrders: {}, payType: 5, chargingSignup: null, payWithCredit: false });
    try {
      const account = await this.props.onLoadAccount(reservationId);
      // Preselección cómoda: todo lo pendiente (el caso más común es cobrar todo).
      // ENI-268: en una mesa gratis los seats no se cobran (el backend los rechaza) — 0.
      const payOrders = {};
      (account.orders || []).filter((o) => !o.paid).forEach((o) => { payOrders[o.purchaseId] = true; });
      // El saldo a favor del titular viaja en el estado de cuenta: se ofrece como medio de pago
      // sólo si tiene, y no hay nada que consultar a mano.
      this.setState({
        account,
        paySeats: account.coverFree ? 0 : ((account.seats && account.seats.pending) || 0),
        payOrders,
      });
      if (this.accountIncluded(account)) this.loadCafeteriaMenu();
    } catch (e) {
      this.setState({ payError: e.message || 'No se pudo cargar la cuenta' });
    }
  };

  // --- Eventos con consumo incluido: lo incluido en la cuenta de la mesa ---
  // El resumen del evento que viaja con la cuenta (`account.eventIncluded`), o null. Null es una
  // mesa común y también "no se pudo calcular" (FR-085): en los dos casos la cuenta se ve y se
  // cobra como siempre, sin "Pasar a incluido".
  accountIncluded = (account) => {
    const included = account ? account.eventIncluded : null;
    return included != null && typeof included === 'object' ? included : null;
  };

  // El menú de cafetería hace falta sólo para el aviso previo de "Pasar a incluido": los ítems de
  // la cuenta no traen su categoría. Se pide una vez, y sólo si se abre la cuenta de una mesa de
  // un evento con inclusiones. Si falla, la confirmación sale sin aviso antes que con uno
  // equivocado (el número que vale es siempre el del server).
  loadCafeteriaMenu = async () => {
    if (this.menuRequested || !this.props.onLoadMenu) return;
    this.menuRequested = true;
    try {
      const menu = await this.props.onLoadMenu();
      this.setState({ cafeteriaMenu: Array.isArray(menu) ? menu : null });
    } catch (e) {
      this.menuRequested = false;
    }
  };

  // Lo que se le pregunta a quien cobra antes de pasar una comanda a incluido (FR-098): qué
  // comanda es, qué efecto tiene y, si corresponde, los mismos avisos que da el POS al cargar.
  includeConfirmText = (account, order) => {
    const items = (order.items || []).map((it) => `${it.quantity} × ${it.name}`).join(', ');
    const title = order.customerName ? `la comanda de ${order.customerName} (#${order.purchaseId})` : `la comanda #${order.purchaseId}`;
    const parts = [
      `Pasar a incluido ${title}${items ? `: ${items}` : ''}.`,
      `Deja de cobrarse ($${order.amount}) y cuenta como entregado del evento.`,
    ];
    const menu = this.state.cafeteriaMenu;
    if (menu) {
      // La comanda ya existe: puede traer un ítem que el menú a mano no tiene y puede entrar
      // antes que otras ya incluidas. `previewCorrection` sólo nombra renglones y números cuando
      // son los que va a dejar el server; si no, se dice que no se pudo anticipar.
      // Lo que se sabe que no entra ni llega acá: `submitInclude` lo corta antes de preguntar.
      const preview = previewCorrection(this.accountIncluded(account), order.items || [], menuByCodeOf(menu));
      const nameOf = (o) => o.name || ((order.items || []).find((it) => Number(it.code) === o.code) || {}).name || `Ítem #${o.code}`;
      if (preview.unverified.length > 0) {
        const sinFicha = preview.unverified.map(nameOf);
        parts.push(`${sinFicha.join(', ')} ya no ${sinFicha.length === 1 ? 'está' : 'están'} en el menú: no se pudo verificar contra lo incluido.`);
      }
      if (preview.overlapping.length > 0) {
        parts.push(`${preview.overlapping.join(', ')} comparten ítems: el reparto lo hace el servidor.`);
      }
      if (preview.uncertain) {
        parts.push('Si no entra entera en lo que queda de lo incluido, el servidor no la pasa.');
      }
    }
    parts.push('¿Pasar a incluido?');
    return parts.join(' ');
  };

  // Qué de la comanda no entra en lo que queda de lo incluido ("2 × Pizza, 3 × Cerveza"), o ''
  // si entra entera o no se puede saber desde acá (sin menú, o con renglones que comparten
  // ítems: `previewCorrection` no nombra un número que el server puede dejar distinto).
  includeOverflowText = (account, order) => {
    const menu = this.state.cafeteriaMenu;
    if (!menu) return '';
    const preview = previewCorrection(this.accountIncluded(account), order.items || [], menuByCodeOf(menu));
    const nameOf = (o) => o.name || ((order.items || []).find((it) => Number(it.code) === o.code) || {}).name || `Ítem #${o.code}`;
    return [
      ...preview.exceeded.map((row) => `${Math.min(row.added, row.delivered - row.allowance)} × ${row.label}`),
      ...preview.outside.map((o) => `${o.quantity} × ${nameOf(o)}`),
    ].join(', ');
  };

  submitInclude = async (order) => {
    const { account, accountBusy, payOrders } = this.state;
    if (!account || accountBusy || !this.accountIncluded(account)) return;
    // Una comanda pasa a incluido ENTERA o no pasa: si con el contador a mano ya se ve que no
    // entra, ni se pregunta ni se le pide al servidor (que la rechazaría con 7609).
    const sobra = this.includeOverflowText(account, order);
    if (sobra) {
      this.setState({ payError: `No se puede pasar a incluido: no entra entera en lo que queda de lo incluido (${sobra}). Anulala y volvé a cargarla.` });
      return;
    }
    if (!window.confirm(this.includeConfirmText(account, order))) return;
    this.setState({ accountBusy: true, payError: null });
    // "Volver" no se deshabilita con el pedido en vuelo: cuando llega la respuesta, la cuenta
    // abierta puede ser la de otra mesa (o ninguna). Lo que vuelve es de ÉSTA y no se pinta
    // encima de la otra: ni la cuenta, ni el error, ni el "ocupado", que `openAccount` ya dejó
    // en falso para la que abrió.
    const stillOpen = () => this.state.accountFor === account.reservationId;
    try {
      const result = await this.props.onIncludeOrder(order.purchaseId);
      // La respuesta trae la cuenta ya recalculada; si no viniera, se vuelve a pedir.
      const updated = (result && result.account) || await this.props.onLoadAccount(account.reservationId);
      // Lo que quedó sin pagar conserva la marca que tenía: pasar una comanda a incluido no
      // cambia qué se iba a cobrar del resto.
      const payOrdersNext = {};
      ((updated && updated.orders) || []).filter((o) => !o.paid).forEach((o) => { payOrdersNext[o.purchaseId] = !!payOrders[o.purchaseId]; });
      if (!stillOpen()) return;
      this.setState({ accountBusy: false, account: updated, payOrders: payOrdersNext });
    } catch (e) {
      if (!stillOpen()) return;
      // El rechazo (76xx) llega con su texto. La cuenta se vuelve a pedir porque el motivo más
      // común es que cambió por otro lado: otro cajero ya la cobró o la pasó a incluido.
      this.setState({ accountBusy: false, payError: e.message || 'No se pudo pasar a incluido' });
      try {
        const fresh = await this.props.onLoadAccount(account.reservationId);
        if (fresh && stillOpen()) {
          const payOrdersNext = {};
          (fresh.orders || []).filter((o) => !o.paid).forEach((o) => { payOrdersNext[o.purchaseId] = !!payOrders[o.purchaseId]; });
          this.setState({ account: fresh, payOrders: payOrdersNext });
        }
      } catch (reloadError) { /* se conserva la cuenta que había */ }
    }
  };

  // El contador del evento, igual en el panel de la mesa y en su cuenta (y en el POS): lo
  // entregado contra lo pactado, con los excedidos resaltados.
  renderIncludedCounter(summary) {
    const counter = formatCounter(summary);
    if (counter.length === 0) return 'sin cantidades pactadas';
    return counter.map((row, i) => (
      <span key={i}>
        {i > 0 ? ' · ' : ''}
        <span className={row.over ? 'text-danger font-weight-bold' : undefined}>
          {row.text}{row.stale ? ' (a revisar)' : ''}
        </span>
      </span>
    ));
  }

  renderIncludedOutside(summary) {
    const outside = (summary && summary.outside) || [];
    if (outside.length === 0) return null;
    return (
      <div className="text-danger" style={{ fontSize: 12 }}>
        Fuera de lo pactado: {outside.map((o) => `${o.quantity} × ${o.name || `Ítem #${o.code}`}`).join(' · ')}
      </div>
    );
  }

  // FR-081: las comandas incluidas van aparte, sin casilla y sin importe a cobrar. Una mesa
  // común no trae ninguna (ni el resumen) y no ve este bloque: su cuenta queda idéntica.
  renderIncludedOrders(account) {
    const summary = this.accountIncluded(account);
    const orders = (account.orders || []).filter((o) => o.included);
    if (!summary && orders.length === 0) return null;
    return (
      <div className="mb-2 salon-account-included">
        <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>Incluido en el evento (sin cargo)</div>
        {summary && (
          <div className="mb-1" style={{ fontSize: 12 }}>
            {this.renderIncludedCounter(summary)}
            {this.renderIncludedOutside(summary)}
          </div>
        )}
        {orders.length === 0 ? (
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>Esta mesa no tiene nada cargado como incluido.</div>
        ) : (
          <ul className="list-unstyled mb-2">
            {orders.map((o) => (
              <li key={o.purchaseId} style={{ fontSize: 13, borderBottom: '0.5px solid #f0f0f0', padding: '4px 0' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    {o.customerName || `Comanda #${o.purchaseId}`}
                    {o.customerName && <span className="text-muted"> · #{o.purchaseId}</span>}
                  </span>
                  <span className="text-muted" style={{ fontSize: 12 }}>${Number(o.listValue) || 0} de lista</span>
                </div>
                <ul className="list-unstyled mb-1" style={{ paddingLeft: 26 }}>
                  {(o.items || []).map((it, idx) => (
                    <li key={idx} className="text-muted" style={{ fontSize: 12 }}>{it.quantity} × {it.name}</li>
                  ))}
                </ul>
                <div className="text-muted" style={{ fontSize: 12, paddingLeft: 26 }}>
                  {includedOriginText(o)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  /**
   * Stepper de personas: de a UNA, en la fila donde vive el número.
   *
   * Reemplaza los dos mini-forms de "Agregar/Quitar persona". El caso real siempre es "se sumó
   * uno" o "se fue uno"; para varios se toca de nuevo. Al sumar, la entrada se CARGA A LA
   * CUENTA (decisión de producto): así el botón hace una sola cosa y no puede equivocarse, y
   * cobrarla en el acto sigue disponible desde "Cobrar parte".
   *
   * El `−` NO se deshabilita: si no se puede, lo dice. Un control gris no explica el motivo, y
   * el motivo acá es plata que ya entró a caja — merece una frase, no un botón apagado.
   */
  stepSeats = async (seated, delta) => {
    if (this.state.seatsBusy) return;
    const id = seated.id;
    if (delta < 0) {
      const motivo = this.removalReason(seated, 1);
      if (motivo) { this.setState({ seatsError: motivo }); return; }
    }
    this.setState({ seatsBusy: true, seatsError: null });
    try {
      if (delta > 0) await this.props.onAddSeats(id, { count: 1, chargeNow: false });
      else           await this.props.onRemoveSeats(id, 1);
      this.setState({ seatsBusy: false });
    } catch (e) {
      this.setState({ seatsBusy: false, seatsError: e.message || 'No se pudo cambiar la cantidad' });
    }
  };

  /**
   * Motivo por el que no se pueden quitar `n` personas, o null. Son DOS razones distintas y hay
   * que distinguirlas: en una mesa sin nada cobrado, "no se puede bajar de 0 porque esas
   * entradas ya se cobraron" no quiere decir nada — lo que pasa ahí es que la mesa se queda sin
   * nadie, que es liberarla. Espeja los dos rechazos del backend, con sus dos mensajes.
   */
  removalReason(seated, n) {
    const total = Number(seated.player_count) || 0;
    const paid  = Number(seated.paid_seats) || 0;
    const quedan = total - n;
    // FR-063 — la hija de una jornada SÍ puede quedar en 0 y seguir sentada: la mesa es del
    // club aunque se hayan ido todos, y vaciarla no es liberarla (liberarla o cancelarla es
    // justo lo que la guarda hermana no deja hacer desde acá). El backend usa el mismo
    // criterio, y por `origin`, no por un parámetro (`removeReservationSeats`: `allowZero =
    // allowEmpty || origin === 'actividad'`). El piso de `paid_seats` de abajo sigue igual.
    const floor = managedBy(seated) === 'actividad' ? 0 : 1;
    if (quedan < floor) {
      return floor === 0
        ? 'No hay tanta gente en la mesa.'
        : 'Así no queda nadie en la mesa. Si no vino nadie, liberala o cancelá la reserva.';
    }
    if (quedan < paid) {
      // Los socios sin cargo cuentan dentro de `paid_seats`: no se sacan con el −, sino con
      // "Quitar socio" en la cuenta de la mesa (libera su lugar de la jornada).
      const { members } = seatBreakdown(seated);
      if (members > 0) {
        return `La mesa tiene ${paid - members} entrada(s) cobrada(s) y ${members} socio(s): no se puede bajar de ${paid}. Para sacar a un socio, usá "Quitar" en Socios.`;
      }
      return paid >= total
        ? `Ya se cobraron las ${paid} entradas de esta mesa. Para devolver una hay que anular la venta a mano.`
        : `No se puede bajar de ${paid}: esas entradas ya se cobraron.`;
    }
    return null;
  }

  // --- Socios en una mesa sentada: "+ Socio" suma una persona sin cargo ---
  // ¿Aplica el lugar de socio? No en la de una jornada (ya es gratis) ni en una mesa gratis. La
  // mesa abierta SÍ (pedido del dueño, 2026-10-17): el socio que cae sin anotarse suma una
  // persona sin cargo, y su lugar cuenta en el cupo como cualquier walk-in.
  memberSeatsApply = (r) => r && !r.activity && managedBy(r) !== 'actividad'
    && Number(r.cover_free) !== 1;

  // El voucher de mesa sigue sin aplicar a la mesa abierta: vende cada lugar a su precio.
  tableVoucherApply = (r) => this.memberSeatsApply(r) && !r.openTable && r.origin !== 'abierta';

  submitMemberSeat = async (reservationId, { pay = false } = {}) => {
    const form = this.state.memberForm || {};
    const cedula = String(form.cedula || '').replace(/\D/g, '');
    if (!cedula || form.busy) return;
    this.setState({ memberForm: { ...form, busy: true, error: null } });
    try {
      const r = await addMemberSeat(reservationId, { cedula, pay });
      this.setState({ memberForm: { reservationId, cedula: '', busy: false, error: null, done: r.free ? 'Socio agregado sin cargo.' : 'Agregado pagando su entrada.' } });
      this.props.onRefresh();
      if (this.state.accountFor === reservationId) this.openAccount(reservationId, { closing: this.state.accountClosing });
    } catch (e) {
      const canAddPaying = !!(e.body && e.body.data && e.body.data.canAddPaying);
      this.setState({ memberForm: { ...form, busy: false, error: e.message || 'No se pudo agregar al socio', canAddPaying } });
    }
  };

  removeMemberFromTable = async (reservationId, member) => {
    if (!window.confirm(`¿Quitar a ${member.name || member.cedula} de la mesa?${member.free ? ' Su lugar sin cargo de hoy queda libre para otra mesa.' : ''}`)) return;
    this.setState({ accountBusy: true, payError: null });
    try {
      await removeMemberSeat(reservationId, member.id);
      this.props.onRefresh();
      await this.openAccount(reservationId, { closing: this.state.accountClosing });
    } catch (e) {
      this.setState({ accountBusy: false, payError: e.message || 'No se pudo quitar al socio' });
    }
  };

  renderMemberSeatRow(seated) {
    if (!this.memberSeatsApply(seated)) return null;
    const { members } = seatBreakdown(seated);
    const form = this.state.memberForm && this.state.memberForm.reservationId === seated.id ? this.state.memberForm : null;
    return (
      <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted">Socios</span>
          <span className="d-flex align-items-center" style={{ gap: 6 }}>
            <span>{members ? `${members} sin cargo` : '—'}</span>
            {!form && (
              <button type="button" className="btn btn-sm btn-white" style={{ lineHeight: 1, padding: '2px 8px' }}
                title="Un socio cuyo plan incluye mesa: suma una persona sin cargo"
                onClick={() => this.setState({ memberForm: { reservationId: seated.id, cedula: '', busy: false, error: null } })}>+ Socio</button>
            )}
          </span>
        </div>
        {form && (
          <div className="mt-2">
            <div className="d-flex" style={{ gap: 6 }}>
              <input type="text" className="form-control form-control-sm" placeholder="Cédula del socio" autoFocus
                value={form.cedula}
                onChange={(e) => this.setState({ memberForm: { ...form, cedula: e.target.value, error: null, canAddPaying: false, done: null } })}
                onKeyDown={(e) => { if (e.key === 'Enter') this.submitMemberSeat(seated.id); }} />
              <button type="button" className="btn btn-sm btn-accent text-nowrap" disabled={form.busy || !String(form.cedula || '').trim()}
                onClick={() => this.submitMemberSeat(seated.id)}>Agregar</button>
              <button type="button" className="btn btn-sm btn-white" onClick={() => this.setState({ memberForm: null })}>✕</button>
            </div>
            {form.done && <small className="text-success d-block mt-1">{form.done}</small>}
            {form.error && (
              <div className="alert alert-warning py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>
                {form.error}
                {form.canAddPaying && (
                  <button type="button" className="btn btn-sm btn-white d-block mt-1" disabled={form.busy}
                    onClick={() => this.submitMemberSeat(seated.id, { pay: true })}>Agregarlo pagando su entrada</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Voucher de mesa: un premio o cortesía "Mesa para N" cubre N lugares sin cargo ---
  // Mismas mesas que el lugar de socio: no la abierta, ni la de una jornada, ni una mesa gratis.
  submitTableVoucher = async (reservationId) => {
    const form = this.state.voucherForm || {};
    const code = String(form.code || '').trim();
    if (!code || form.busy) return;
    this.setState({ voucherForm: { ...form, busy: true, error: null, done: null } });
    try {
      const r = await applyTableVoucher(reservationId, code);
      const sumadas = r.addedSeats > 0 ? ` Se sumaron ${r.addedSeats} lugar(es) a la mesa.` : '';
      this.setState({ voucherForm: { reservationId, code: '', busy: false, error: null, done: `${r.label || `Mesa para ${r.seats}`}: ${r.seats} lugar(es) sin cargo.${sumadas}` } });
      this.props.onRefresh();
      if (this.state.accountFor === reservationId) this.openAccount(reservationId, { closing: this.state.accountClosing });
    } catch (e) {
      this.setState({ voucherForm: { ...form, busy: false, error: e.message || 'No se pudo aplicar el voucher' } });
    }
  };

  renderTableVoucherRow(seated) {
    if (!this.tableVoucherApply(seated)) return null;
    const form = this.state.voucherForm && this.state.voucherForm.reservationId === seated.id ? this.state.voucherForm : null;
    return (
      <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted">Premio o cortesía</span>
          {!form && (
            <button type="button" className="btn btn-sm btn-white" style={{ lineHeight: 1, padding: '2px 8px' }}
              title="Un voucher «Mesa para N»: cubre N lugares sin cargo"
              onClick={() => this.setState({ voucherForm: { reservationId: seated.id, code: '', busy: false, error: null } })}>+ Voucher de mesa</button>
          )}
        </div>
        {form && (
          <div className="mt-2">
            <div className="d-flex" style={{ gap: 6 }}>
              <input type="text" className="form-control form-control-sm" placeholder="Código del voucher" aria-label="Código del voucher de mesa" autoFocus
                value={form.code}
                onChange={(e) => this.setState({ voucherForm: { ...form, code: e.target.value, error: null, done: null } })}
                onKeyDown={(e) => { if (e.key === 'Enter') this.submitTableVoucher(seated.id); }} />
              <button type="button" className="btn btn-sm btn-accent text-nowrap" disabled={form.busy || !String(form.code || '').trim()}
                onClick={() => this.submitTableVoucher(seated.id)}>Aplicar</button>
              <button type="button" className="btn btn-sm btn-white" onClick={() => this.setState({ voucherForm: null })}>✕</button>
            </div>
            {form.done && <small className="text-success d-block mt-1">{form.done}</small>}
            {form.error && <div className="alert alert-warning py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>{form.error}</div>}
          </div>
        )}
      </div>
    );
  }

  // --- Validar llegada con el headcount real ---
  // Sin tope por arriba (que vengan más de los reservados es normal, igual que agregar gente a
  // una mesa abierta); por abajo, el piso son las entradas YA COBRADAS —y al menos 1, porque que
  // no venga nadie es cancelar la reserva, no sentarla—. Bajar de lo cobrado no es una decisión
  // de UX: no hay reversa de caja (removeReservationSeats lo rechaza), así que sin este piso el
  // mostrador escribe "3", aprieta Sentar y el backend le contesta que no, con el cliente
  // enfrente. Con el cobro por adelantado esto pasó de raro a corriente.
  stepCheckIn = (delta, minSeats = 1) => {
    const floor = Math.max(1, Number(minSeats) || 1);
    this.setState({ checkInCount: Math.max(floor, (Number(this.state.checkInCount) || 1) + delta) });
  };

  /**
   * "¿Cuántos vinieron?" — el paso de validar la llegada de una reserva.
   *
   * Vive suelto porque se abre desde DOS lados: el panel de la mesa reservada (cuando falta poco
   * para la hora) y el form de sentar de una mesa que todavía figura libre — el cliente que llega
   * una hora antes. Duplicarlo ahí habría dejado dos pisos de `paid_seats` distintos, que es
   * justo lo que protege de cobrarle dos veces a quien ya pagó.
   */
  renderArrivalForm(r) {
    const { pricePerPlayer } = this.sch();
    const paidSeats = Number(r.paid_seats) || 0;
    return (
      <div className="mt-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        <label className="d-block mb-1"><small>¿Cuántos vinieron?</small></label>
        <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
          <button type="button" className="btn btn-white btn-sm" disabled={this.state.checkInCount <= Math.max(1, paidSeats)}
            onClick={() => this.stepCheckIn(-1, paidSeats)}>–</button>
          <div style={{ minWidth: 32, textAlign: 'center', fontSize: 18, fontWeight: 500 }}>{this.state.checkInCount}</div>
          <button type="button" className="btn btn-white btn-sm" onClick={() => this.stepCheckIn(1, paidSeats)}>+</button>
          <span className="text-muted ml-1" style={{ fontSize: 12 }}>de {r.player_count} reservadas</span>
        </div>
        <p className="text-muted mb-2" style={{ fontSize: 12 }}>
          {Number(r.cover_free) === 1
            ? 'Mesa gratis: no se cobra entrada.'
            : `Entradas a cobrar: $${Math.max(0, (Number(this.state.checkInCount) || 0) - paidSeats) * pricePerPlayer}`}
        </p>
        {/* Reserva pagada por adelantado (transferencia antes de venir): explicar el piso
            acá, y no dejar que el operador lo descubra por un rechazo del backend. */}
        {paidSeats > 0 && (() => {
          // `paid_seats` incluye a los socios sin cargo: el piso es el mismo, el texto los separa.
          const b = seatBreakdown(r);
          const partes = [];
          if (b.paid > 0) partes.push(`${b.paid} entrada${b.paid > 1 ? 's' : ''} ya cobrada${b.paid > 1 ? 's' : ''}`);
          if (b.members > 0) partes.push(`${b.members} socio${b.members > 1 ? 's' : ''} sin cargo`);
          return (
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              {partes.join(' y ')}: la mesa no puede quedar en menos de {paidSeats}
              {b.paid > 0 ? ' (no hay devolución desde el sistema).' : '.'}
            </p>
          );
        })()}
        {this.state.checkInError && (
          <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{this.state.checkInError}</div>
        )}
        <div className="d-flex" style={{ gap: 6 }}>
          <button type="button" className="btn btn-accent btn-sm flex-grow-1" disabled={this.state.checkInBusy}
            onClick={() => this.submitCheckIn(r.id)}>Sentar</button>
          <button type="button" className="btn btn-white btn-sm flex-grow-1"
            onClick={() => this.setState({ checkingIn: null, checkInError: null })}>Cancelar</button>
        </div>
      </div>
    );
  }

  submitCheckIn = async (reservationId) => {
    const count = Math.trunc(Number(this.state.checkInCount) || 0);
    if (count < 1) return;
    this.setState({ checkInBusy: true, checkInError: null });
    try {
      await this.props.onCheckIn(reservationId, count);
      this.setState({ checkingIn: null, checkInBusy: false });
    } catch (e) {
      this.setState({ checkInBusy: false, checkInError: e.message || 'No se pudo validar la llegada' });
    }
  };

  // --- Walk-in sobre una mesa abierta (FR-054) ---
  // Sin tope: el cupo es el límite de lo que se vende online, y desde el salón se puede sentar
  // gente por encima (el backend lo cuenta en `taken`). Los lugares libres (`cupo - taken`,
  // con los holds de pago online incluidos) se muestran como guía y se avisa al pasarse.
  stepWalkIn = (delta) => {
    const next = (Number(this.state.checkInCount) || 1) + delta;
    this.setState({ checkInCount: Math.max(1, next) });
  };

  /**
   * FR-054 — sentar walk-ins en la mesa abierta desde el salón.
   *
   * Va por el MISMO endpoint que "Validar llegada" (checkInReservation), que recibe el
   * headcount ABSOLUTO: se manda `player_count + walkIns`, nunca los walk-ins solos. Los
   * lugares de las inscripciones confirmadas ya están en `player_count` desde que se
   * confirmaron (aunque sus dueños no hayan llegado), así que mandar el delta pelado le
   * QUITARÍA lugares a la mesa y devolvería cupo vendido.
   *
   * Las entradas de los walk-ins quedan pendientes en la cuenta de la mesa (FR-052): se cobran
   * al liberarla o desde "Cobrar parte", igual que las inscripciones en modo libre.
   */
  submitWalkIn = async (reservation) => {
    const walkIns = Math.trunc(Number(this.state.checkInCount) || 0);
    if (walkIns < 1) return;
    const seated = Math.max(0, Number(reservation.player_count) || 0);
    this.setState({ checkInBusy: true, checkInError: null });
    try {
      await this.props.onCheckIn(reservation.id, seated + walkIns);
      this.setState({ checkingIn: null, checkInBusy: false });
    } catch (e) {
      this.setState({ checkInBusy: false, checkInError: e.message || 'No se pudo sentar el walk-in' });
    }
  };

  /**
   * El socio que cae a la mesa abierta sin anotarse, con la mesa todavía sin sentar: se lo suma
   * con su lugar sin cargo (mismo endpoint que "+ Socio" de la mesa sentada) y se sienta la mesa
   * con el headcount que quedó. Sentada, el resto entra por "+ Socio" del panel de la mesa.
   */
  submitMemberWalkIn = async (reservation, { pay = false } = {}) => {
    const form = this.state.memberWalkIn || {};
    const cedula = String(form.cedula || '').replace(/\D/g, '');
    if (!cedula || form.busy) return;
    this.setState({ memberWalkIn: { ...form, busy: true, error: null, canAddPaying: false } });
    let added;
    try {
      added = await addMemberSeat(reservation.id, { cedula, pay });
    } catch (e) {
      const canAddPaying = !!(e.body && e.body.data && e.body.data.canAddPaying);
      this.setState({ memberWalkIn: { ...form, busy: false, error: e.message || 'No se pudo agregar al socio', canAddPaying } });
      return;
    }
    try {
      const players = Number(added && added.reservation && added.reservation.player_count) || (Number(reservation.player_count) || 0) + 1;
      await this.props.onCheckIn(reservation.id, players);
      this.setState({ memberWalkIn: null });
    } catch (e) {
      // El socio ya quedó en la mesa: lo que falló es sentarla.
      this.props.onRefresh();
      this.setState({ memberWalkIn: { ...form, cedula: '', busy: false, error: `El socio quedó anotado, pero no se pudo sentar la mesa: ${e.message || 'error de conexión'}` } });
    }
  };

  renderMemberWalkIn(r) {
    if (!this.memberSeatsApply(r)) return null; // mesa abierta gratis: el backend lo rechaza con el motivo
    const form = this.state.memberWalkIn && this.state.memberWalkIn.reservationId === r.id ? this.state.memberWalkIn : null;
    if (!form) {
      return (
        <button type="button" className="btn btn-white btn-block btn-sm mt-2"
          title="Un socio cuyo plan incluye mesa ese día: se sienta sin cargo"
          onClick={() => this.setState({ memberWalkIn: { reservationId: r.id, cedula: '', busy: false, error: null } })}>
          Sentar socio sin cargo
        </button>
      );
    }
    return (
      <div className="mt-2 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        <label className="d-block mb-1"><small>Cédula del socio</small></label>
        <div className="d-flex" style={{ gap: 6 }}>
          <input type="text" className="form-control form-control-sm" placeholder="Cédula" autoFocus
            value={form.cedula}
            onChange={(e) => this.setState({ memberWalkIn: { ...form, cedula: e.target.value, error: null, canAddPaying: false } })}
            onKeyDown={(e) => { if (e.key === 'Enter') this.submitMemberWalkIn(r); }} />
          <button type="button" className="btn btn-sm btn-accent text-nowrap" disabled={form.busy || !String(form.cedula || '').trim()}
            onClick={() => this.submitMemberWalkIn(r)}>Sentar</button>
          <button type="button" className="btn btn-sm btn-white" disabled={form.busy}
            onClick={() => this.setState({ memberWalkIn: null })}>✕</button>
        </div>
        {form.error && (
          <div className="alert alert-warning py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>
            {form.error}
            {form.canAddPaying && (
              <button type="button" className="btn btn-sm btn-white d-block mt-1" disabled={form.busy}
                onClick={() => this.submitMemberWalkIn(r, { pay: true })}>Sentarlo pagando su entrada</button>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Cambiar de mesa ---
  // Candidatas = las que el salón pinta como libres (excluye ocupadas y bloqueadas). El backend
  // revalida igual: entre que se pinta el combo y se confirma, otra caja pudo sentar a alguien.
  freeTables = (excludeId) => (this.props.agenda.tables || [])
    .filter((t) => t.id !== excludeId && this.tableModel(t).state === 'libre')
    .sort((a, b) => (a.capacity - b.capacity) || (a.id - b.id));

  submitMove = async (reservation) => {
    const tableId = Number(this.state.moveTableId);
    if (!tableId) return;
    const count = Number(this.state.moveCount) || 0; // 0 = toda la mesa
    // Revalidar contra la reserva VIVA: si mientras el form estaba abierto se fue gente (o
    // otra caja movió), un count viejo >= player_count el backend lo promueve a mover la mesa
    // ENTERA — una escalada silenciosa que el operador no pidió.
    const players = Number(reservation.player_count) || 0;
    if (count > 0 && count >= players) {
      this.setState({ moveCount: 0, moveError: 'La mesa cambió: elegí de nuevo cuántos se mudan' });
      return;
    }
    this.setState({ moveBusy: true, moveError: null });
    try {
      await this.props.onMove(reservation.id, tableId, count > 0 ? count : undefined);
      this.setState({ movingFrom: null, moveBusy: false, moveTableId: '', moveCount: 0 });
    } catch (e) {
      this.setState({ moveBusy: false, moveError: e.message || 'No se pudo cambiar de mesa' });
    }
  };

  // Bloque "Cambiar de mesa", compartido por la mesa ocupada y la reservada: mudarse antes de
  // llegar (el cliente pidió otra mesa) es tan real como mudarse a mitad de la partida.
  // Chip del encabezado: cambiar de mesa es una propiedad de la MESA (el título), no una acción
  // sobre la cuenta, así que vive arriba y no en la pila de botones de abajo.
  renderMoveChip(reservation) {
    const open = this.state.movingFrom === reservation.id;
    return (
      <button type="button" className="btn btn-sm btn-white" style={{ fontSize: 12, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}
        onClick={() => this.setState(open
          ? { movingFrom: null, moveError: null }
          : { movingFrom: reservation.id, moveTableId: '', moveCount: 0, moveError: null })}>
        {open ? '✕' : '⇄ mover'}
      </button>
    );
  }

  // ENI-269: mesas con gente sentada, candidatas a un MERGE parcial. Sólo del mismo tipo de
  // cobro (gratis con gratis, paga con paga): el backend rechaza la mezcla, mejor no ofrecerla.
  //
  // FR-070 — y con `activityId`, sólo las HERMANAS (misma jornada). Fundir una hija con un
  // walk-in dejaría una sola fila que es mitad jornada y mitad salón, sin forma de separarlas
  // después; el backend lo rechaza y acá directamente no se ofrece. La regla corre para los
  // dos lados: desde una reserva común, una hija sentada tampoco es destino.
  //
  // El corte va por `activity_id` (la columna) y no por el adjunto `activity`: el adjunto es
  // best-effort y su ausencia no puede convertir a una hija en candidata.
  seatedTables = (excludeId, coverFree, activityId) => (this.props.agenda.tables || [])
    .map((t) => ({ table: t, seated: this.tableModel(t).seated }))
    .filter(({ table, seated }) => {
      if (table.id === excludeId || !seated) return false;
      if (Number(seated.cover_free || 0) !== Number(coverFree || 0)) return false;
      const destActivityId = seated.activity_id == null ? null : Number(seated.activity_id);
      if (activityId) return destActivityId === Number(activityId);
      return destActivityId == null;
    })
    .sort((a, b) => (a.table.capacity - b.table.capacity) || (a.table.id - b.table.id));

  renderMove(reservation, currentTableId) {
    if (this.state.movingFrom !== reservation.id) return null;
    const players = Number(reservation.player_count) || 0;
    // Mover PARTE sólo tiene sentido con gente sentada (redistribución en vivo) y si queda
    // al menos una persona en cada mesa.
    const canSplit = reservation.status === 'seated' && players > 1;
    // Clamp contra el player_count vivo: si bajó con el form abierto, un count guardado que
    // quedó fuera de rango no debe seguir ofreciéndose (submitMove lo re-chequea igual).
    // Los socios sin cargo no se reparten (el backend lo rechaza): se mueven hasta los que pagan.
    const movable = Math.min(players - 1, players - seatBreakdown(reservation).members);
    const moveCount = Math.min(Number(this.state.moveCount) || 0, Math.max(0, movable)); // 0 = toda la mesa
    const partial = canSplit && moveCount > 0;

    const free = this.freeTables(currentTableId);
    // Para un parcial también se puede sumar gente a una mesa YA sentada (merge). Si esta
    // reserva es hija de una jornada, las candidatas son sólo sus hermanas (FR-070); las mesas
    // libres se siguen ofreciendo como hoy (el split nace como hija hermana en la mesa nueva).
    const activityId = reservation.activity_id == null ? null : Number(reservation.activity_id);
    const seatedOpts = partial
      ? this.seatedTables(currentTableId, Number(reservation.cover_free) || 0, activityId)
      : [];
    const noOptions = free.length === 0 && seatedOpts.length === 0;
    return (
      <div className="mt-2 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
        {canSplit && (
          <div className="mb-2">
            <label className="d-block mb-1"><small>¿Quiénes se mudan?</small></label>
            <select className="form-control form-control-sm" value={moveCount}
              onChange={(e) => this.setState({ moveCount: Number(e.target.value), moveTableId: '' })}>
              <option value={0}>Toda la mesa ({players} personas)</option>
              {Array.from({ length: Math.max(0, movable) }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n} persona{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        )}
        <label className="d-block mb-1"><small>Se mudan a…</small></label>
        {noOptions ? (
          <p className="text-muted mb-2" style={{ fontSize: 12 }}>No hay ninguna mesa libre ahora mismo.</p>
        ) : (
          <select className="form-control form-control-sm mb-2" value={this.state.moveTableId || ''}
            onChange={(e) => this.setState({ moveTableId: e.target.value })}>
            <option value="">Elegí la mesa</option>
            {free.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.capacity} personas{t.is_vip ? ' · VIP' : ''}
              </option>
            ))}
            {seatedOpts.map(({ table, seated }) => (
              <option key={table.id} value={table.id}>
                {/* En una hermana el `name` ES el nombre de la jornada (el backend lo copia a
                    la hija, FR-057): "6-B · Club de los martes (4p)". */}
                {table.label} · {activityId ? '' : 'con '}{seated.name || 'gente'} ({seated.player_count}p) — se suman ahí
              </option>
            ))}
          </select>
        )}
        <p className="text-muted mb-2" style={{ fontSize: 11 }}>
          {partial
            ? 'Se llevan su parte de la cuenta: las entradas impagas se cobran en la mesa nueva. Comandas y juegos quedan acá.'
            : 'Se lleva todo: entradas, consumos y juegos de la mesa.'}
        </p>
        {this.state.moveError && (
          <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{this.state.moveError}</div>
        )}
        {/* Sin "Cancelar": el chip del encabezado pasa a ✕ y cierra. */}
        <button type="button" className="btn btn-accent btn-sm btn-block"
          disabled={this.state.moveBusy || !this.state.moveTableId}
          onClick={() => this.submitMove(reservation)}>
          {partial ? `Mover ${moveCount} persona${moveCount > 1 ? 's' : ''}` : 'Mover'}
        </button>
      </div>
    );
  }

  closeAccount = () => this.setState({ accountFor: null, accountClosing: false, account: null, payError: null, chargingSignup: null });

  // Recarga la cuenta SIN salir del panel (tras cobrar una inscripción): mismos pasos que
  // openAccount, conservando la intención con la que se abrió. También refresca la agenda,
  // que muestra el paid_seats de la mesa.
  reloadAccount = () => {
    if (this.state.accountFor) this.openAccount(this.state.accountFor, { closing: this.state.accountClosing });
    this.props.onRefresh();
  };

  payTotal = () => {
    const { account, paySeats, payOrders } = this.state;
    if (!account) return 0;
    const seatsAmount = Number(paySeats || 0) * ((account.seats && account.seats.unitPrice) || 0);
    const ordersAmount = (account.orders || [])
      .filter((o) => !o.paid && payOrders[o.purchaseId])
      .reduce((acc, o) => acc + o.amount, 0);
    return seatsAmount + ordersAmount;
  };

  // Saldo a favor del titular de la mesa. Viene con el estado de cuenta: el cliente ya está
  // identificado por la reserva, así que no hay ninguna cédula que volver a pedir.
  creditBalance = () => {
    const account = this.state.account;
    return account && account.customerCedula ? Number(account.storeCredit || 0) : 0;
  };

  // Se usa TODO lo que alcance de lo que se está cobrando; el resto va con el medio elegido.
  appliedCredit = () => (this.state.payWithCredit
    ? Math.min(this.creditBalance(), this.payTotal())
    : 0);

  creditCoversAll = () => this.state.payWithCredit && this.payTotal() > 0 && this.appliedCredit() >= this.payTotal();

  onChangePayment = (value) => {
    if (value === 'credit') { this.setState({ payWithCredit: true }); return; }
    this.setState({ payWithCredit: false, payType: Number(value) });
  };

  // ¿Este cobro deja la cuenta en cero? Sólo entonces tiene sentido encadenar el liberado.
  willSettleAll = () => {
    const { account, paySeats, payOrders } = this.state;
    if (!account) return false;
    // ENI-268: los seats de una mesa gratis no se cobran nunca — no cuentan como pendientes.
    const pendingSeats = account.coverFree ? 0 : ((account.seats && account.seats.pending) || 0);
    const pendingOrders = (account.orders || []).filter((o) => !o.paid);
    return Number(paySeats) === pendingSeats && pendingOrders.every((o) => payOrders[o.purchaseId]);
  };

  submitSettle = async () => {
    const { account, paySeats, payOrders, payType, accountBusy } = this.state;
    if (!account || accountBusy) return;
    const purchaseIds = (account.orders || [])
      .filter((o) => !o.paid && payOrders[o.purchaseId])
      .map((o) => o.purchaseId);
    if (!Number(paySeats) && purchaseIds.length === 0) {
      this.setState({ payError: 'Elegí qué cobrar' });
      return;
    }
    this.setState({ accountBusy: true, payError: null });
    try {
      const release = this.state.accountClosing && this.willSettleAll();
      const credit = this.appliedCredit();
      // Si el saldo cubre todo no queda plata que cobrar: el ticket se emite en EFECTIVO, el
      // balde más inocuo del arqueo (uno de $0 en transferencia pediría un comprobante que no
      // existe). El backend usa el saldo del titular de la reserva.
      const effectiveType = this.creditCoversAll() ? 1 : Number(payType);
      const result = await this.props.onSettleAccount(account.reservationId, {
        seats: Number(paySeats) || 0, purchaseIds, paymentType: effectiveType,
        ...(effectiveType === 5 && this.state.posDeviceId
          ? { posDeviceId: Number(this.state.posDeviceId) } : {}),
        // Saldo a favor: se reparte entre lo que se está cobrando (consumo primero, entradas después).
        ...(credit > 0 ? { creditApplied: credit } : {}),
      }, { release });
      // Liberada: salir del panel de cuenta. Si no se limpia accountFor, la vista se queda
      // clavada en la cuenta de una reserva ya completada mientras la mesa se pinta libre.
      if (result && result.released) {
        this.setState({ accountFor: null, accountClosing: false, account: null, accountBusy: false, payError: null, selectedTableId: null });
        return;
      }
      const updated = (result && result.account) || null;
      const payOrdersNext = {};
      (updated && updated.orders ? updated.orders : []).filter((o) => !o.paid).forEach((o) => { payOrdersNext[o.purchaseId] = true; });
      this.setState({
        accountBusy: false,
        account: updated,
        paySeats: (updated && !updated.coverFree && updated.seats && updated.seats.pending) || 0,
        payOrders: payOrdersNext,
        // El saldo nuevo viene en el estado de cuenta recién devuelto; el medio vuelve a los
        // medios normales para que un segundo cobro no dé por hecho que queda saldo.
        payWithCredit: false,
      });
    } catch (e) {
      this.setState({ accountBusy: false, payError: e.message || 'No se pudo cobrar' });
    }
  };

  // Chip de pago por inscripción (FR-059): lo operativo es Paga/Pendiente. La cuenta sólo
  // recibe confirmadas y no presentadas, así que el único estado que cae al label legible de
  // SIGNUP_STATUS es "No vino" (las por devolver y las vencidas no llegan a esta pantalla).
  signupChip = (s) => {
    if (s.paid || s.status === 'confirmed') return signupPaymentChip(s);
    return SIGNUP_STATUS[s.status] || { label: s.status, badge: 'badge-light' };
  };

  // FR-059 (enmienda For Test): la cuenta de una MESA ABIERTA desglosa las entradas por
  // inscripción — quién, cuántos lugares, si pagó — y deja cobrar una pendiente puntual
  // (mismo mini-modal que "Llegó" en inscripciones — S-08a). Una mesa común no trae
  // openTableSignups y no ve este bloque: su cuenta queda idéntica.
  renderSignupEntries(account) {
    const signups = account.openTableSignups;
    if (!Array.isArray(signups) || signups.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>Entradas por inscripción</div>
        <ul className="list-unstyled mb-1">
          {signups.map((s) => {
            const chip = this.signupChip(s);
            const seats = Number(s.seats) || 0;
            return (
              <li key={s.id} className="d-flex justify-content-between align-items-center" style={{ fontSize: 13, borderBottom: '0.5px solid #f0f0f0', padding: '4px 0' }}>
                <span>
                  {s.name || 'Sin nombre'}
                  <span className="text-muted"> · {seats} lugar{seats === 1 ? '' : 'es'}</span>
                  {s.memberSeat && <span className="text-success"> · 1 socio sin cargo</span>}
                </span>
                <span className="d-flex align-items-center" style={{ gap: 6 }}>
                  <span className={`badge ${chip.badge}`}>{chip.label}</span>
                  {s.status === 'confirmed' && !s.paid && (
                    <button type="button" className="btn btn-sm btn-white" style={{ lineHeight: 1.2, padding: '2px 8px' }}
                      onClick={() => this.setState({ chargingSignup: s })}>Cobrar</button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  renderAccountPanel() {
    const { account, paySeats, payOrders, payType, accountBusy, payError } = this.state;
    if (!account) {
      return (
        <div>
          <div className="text-muted" style={{ fontSize: 13 }}>Cargando cuenta…</div>
          {payError && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: 13 }}>{payError}</div>}
          <button type="button" className="btn btn-white btn-block btn-sm mt-3" onClick={this.closeAccount}>Volver</button>
        </div>
      );
    }
    const seats = account.seats || {};
    const pendingOrders = (account.orders || []).filter((o) => !o.paid);
    const total = this.payTotal();
    const eventIncluded = this.accountIncluded(account);
    return (
      <div>
        <div style={{ fontWeight: 500, fontSize: 16 }}>Cuenta de {account.tableLabel || 'la mesa'}</div>
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Pendiente ${(account.totals && account.totals.pending) || 0} · pagado ${(account.totals && account.totals.paid) || 0}
        </div>

        {/* Socios anotados en la mesa (sólo viene si hay): los sin cargo y los que pagan su
            entrada. Quitar a uno libera su lugar de la jornada. */}
        {(account.members || []).length > 0 && (
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>Socios</div>
            <ul className="list-unstyled mb-0">
              {account.members.map((m) => (
                <li key={m.id} className="d-flex justify-content-between align-items-center" style={{ fontSize: 13, padding: '2px 0' }}>
                  <span>
                    {m.name || m.cedula}
                    <small className="text-muted"> · {m.free ? 'sin cargo' : 'paga su entrada'}</small>
                  </span>
                  {/* El lugar sin cargo de una inscripción a mesa abierta es de la inscripción:
                      se va con ella, no desde acá. */}
                  {m.fromSignup ? (
                    <small className="text-muted">por su inscripción</small>
                  ) : (
                    <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} disabled={accountBusy}
                      onClick={() => this.removeMemberFromTable(account.reservationId, m)}>Quitar</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>Lugares (entradas)</div>
        {/* ENI-268: mesa gratis — no hay entradas para cobrar y el backend rechaza el intento,
            así que el stepper directamente no se ofrece. */}
        {account.coverFree ? (
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>Mesa gratis: las entradas no se cobran.</div>
        ) : seats.pending > 0 ? (
          <div className="d-flex align-items-center mb-2">
            <button type="button" className="btn btn-sm btn-white"
              onClick={() => this.setState({ paySeats: Math.max(0, Number(paySeats) - 1) })}>–</button>
            <span style={{ minWidth: 30, textAlign: 'center', fontWeight: 500 }}>{paySeats}</span>
            <button type="button" className="btn btn-sm btn-white"
              onClick={() => this.setState({ paySeats: Math.min(seats.pending, Number(paySeats) + 1) })}>+</button>
            <span className="text-muted ml-2" style={{ fontSize: 12 }}>
              de {seats.pending} sin pagar · ${seats.unitPrice} c/u
            </span>
          </div>
        ) : (
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>Todos los lugares están pagos.</div>
        )}

        {this.renderSignupEntries(account)}

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>Comandas sin pagar</div>
        {pendingOrders.length === 0 ? (
          <div className="text-muted mb-2" style={{ fontSize: 12 }}>No hay consumo pendiente.</div>
        ) : (
          <ul className="list-unstyled mb-2">
            {pendingOrders.map((o) => {
              const open = !!(this.state.openOrders || {})[o.purchaseId];
              // Título reconocible: el nombre con el que se tomó la comanda; el id pelado
              // no le dice nada a quien cobra, así que va como referencia secundaria.
              const title = o.customerName || `Comanda #${o.purchaseId}`;
              return (
                <li key={o.purchaseId} style={{ fontSize: 13, borderBottom: '0.5px solid #f0f0f0', padding: '4px 0' }}>
                  <div className="d-flex align-items-center">
                    <input type="checkbox" className="mr-2" checked={!!payOrders[o.purchaseId]}
                      onChange={(e) => this.setState({ payOrders: { ...payOrders, [o.purchaseId]: e.target.checked } })} />
                    <button type="button"
                      className="btn btn-link p-0 text-left flex-grow-1 d-flex justify-content-between align-items-center"
                      style={{ fontSize: 13, textDecoration: 'none', color: 'inherit' }}
                      onClick={() => this.setState({ openOrders: { ...(this.state.openOrders || {}), [o.purchaseId]: !open } })}>
                      <span>
                        {open ? '▾' : '▸'} {title}
                        {o.customerName && <span className="text-muted"> · #{o.purchaseId}</span>}
                      </span>
                      <span style={{ fontWeight: 500 }}>${o.amount}</span>
                    </button>
                    {/* FR-098: el pedido que se cargó a la mesa por error y era de lo incluido. */}
                    {eventIncluded && (
                      <button type="button" className="btn btn-sm btn-white ml-2" style={{ lineHeight: 1.2, padding: '2px 8px', whiteSpace: 'nowrap' }}
                        disabled={accountBusy} onClick={() => this.submitInclude(o)}>Pasar a incluido</button>
                    )}
                  </div>
                  {open && (
                    <ul className="list-unstyled mb-1" style={{ paddingLeft: 26 }}>
                      {(o.items || []).length === 0 ? (
                        <li className="text-muted" style={{ fontSize: 12 }}>Sin detalle de ítems.</li>
                      ) : o.items.map((it, idx) => (
                        <li key={idx} className="d-flex justify-content-between text-muted" style={{ fontSize: 12 }}>
                          <span>{it.quantity} × {it.name}</span>
                          <span>${it.amount}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {this.renderIncludedOrders(account)}

        <label className="d-block mb-1 mt-2"><small>Pago</small></label>
        {/* El saldo a favor del titular es un medio de pago más, y sólo aparece si tiene saldo. */}
        <select className="form-control form-control-sm mb-2"
          value={this.state.payWithCredit ? 'credit' : payType}
          onChange={(e) => this.onChangePayment(e.target.value)}>
          {SETTLE_PAYMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          {this.creditBalance() > 0 && (
            <option value="credit">Saldo a favor (${this.creditBalance()})</option>
          )}
        </select>
        {/* Si el saldo no llega a cubrir lo que se está cobrando, el resto se cobra con otro
            medio: se elige acá mismo, sin deshacer el saldo. */}
        {this.state.payWithCredit && !this.creditCoversAll() && (
          <React.Fragment>
            <label className="d-block mb-1"><small>El saldo no alcanza — cobrar ${total - this.appliedCredit()} con:</small></label>
            <select className="form-control form-control-sm mb-2" value={payType}
              onChange={(e) => this.setState({ payType: Number(e.target.value) })}>
              {SETTLE_PAYMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </React.Fragment>
        )}
        {/* F1 del arqueo: una mesa se paga a veces en el lector de la TIENDA. Sin registrar cuál
            fue, el cierre de lote de cada aparato no se puede conciliar — es el caso que descuadra. */}
        {payType === 5 && !this.creditCoversAll() && this.state.posDevices.length > 1 && (
          <select className="form-control form-control-sm mb-2" value={this.state.posDeviceId}
            onChange={(e) => this.setState({ posDeviceId: e.target.value })}>
            {this.state.posDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}

        <div className="d-flex justify-content-between align-items-baseline" style={{ borderTop: '0.5px solid #eee', paddingTop: 10 }}>
          <span className="text-muted" style={{ fontSize: 13 }}>{this.appliedCredit() > 0 ? 'Cuenta' : 'A cobrar'}</span>
          <span style={{ fontWeight: 500 }}>${total}</span>
        </div>
        {this.appliedCredit() > 0 && (
          <React.Fragment>
            <div className="d-flex justify-content-between align-items-baseline" style={{ color: '#1c7a48' }}>
              <span style={{ fontSize: 13 }}>Saldo a favor</span>
              <span style={{ fontWeight: 500 }}>−${this.appliedCredit()}</span>
            </div>
            <div className="d-flex justify-content-between align-items-baseline">
              <span className="text-muted" style={{ fontSize: 13 }}>A cobrar</span>
              <span style={{ fontWeight: 500 }}>${total - this.appliedCredit()}</span>
            </div>
            {this.creditCoversAll() && (
              <div className="text-muted" style={{ fontSize: 12 }}>
                Lo cubre el saldo. Le quedan ${Math.max(0, this.creditBalance() - this.appliedCredit())} a favor.
              </div>
            )}
          </React.Fragment>
        )}

        {payError && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: 13 }}>{payError}</div>}

        <button type="button" className="btn btn-accent btn-block btn-sm mt-2" disabled={accountBusy || total <= 0}
          onClick={this.submitSettle}>
          {accountBusy
            ? '...'
            : (this.state.accountClosing && this.willSettleAll())
              ? `Cobrar $${this.payTotal() - this.appliedCredit()} y liberar la mesa`
              : 'Cobrar seleccionado'}
        </button>
        {/* Salida para el camino parcial que terminó cobrando todo: sin esto hay que volver
            al panel de la mesa para encontrar el botón. */}
        {account && account.totals && Number(account.totals.pending) === 0 && (
          <button type="button" className="btn btn-accent btn-block btn-sm mt-2" disabled={accountBusy}
            onClick={() => this.props.onRelease(account.reservationId)}>Liberar mesa</button>
        )}
        <button type="button" className="btn btn-white btn-block btn-sm mt-2" onClick={this.closeAccount}>Volver</button>
        {this.state.chargingSignup && (
          <OpenTableChargeModal
            show
            signup={this.state.chargingSignup}
            unitPrice={(account.seats && account.seats.unitPrice) || null}
            mode="charge"
            onDone={this.reloadAccount}
            onRefresh={this.reloadAccount}
            onClose={() => this.setState({ chargingSignup: null })}
          />
        )}
      </div>
    );
  }

  // --- "Sentar la jornada" (FR-068, FR-074) ---
  // El stepper arranca en 0 y puede quedarse ahí: "sólo sentar" es el caso normal cuando el
  // mostrador todavía no redimió ninguna entrada. Sin tope por arriba —el salón cuenta a ojo
  // la gente que ya está en la mesa— y sin piso más allá de 0: acá no hay plata de por medio
  // (las hijas nacen gratis) y las entradas se cobran en Anotados, no en esta mesa.
  stepActivitySeat = (delta) => {
    this.setState({ activityCount: Math.max(0, (Number(this.state.activityCount) || 0) + delta) });
  };

  /**
   * Sienta la jornada ENTERA desde esta mesa (cascada a las hermanas, FR-060) y pone el head
   * count en ESTA. El `headcount` viaja siempre explícito, incluso en 0: omitirlo significa
   * otra cosa (que el backend reparta los `redeemed` del evento), y ese es el botón de la
   * pantalla de la jornada, no el del salón.
   */
  submitStartActivity = async (r, tableId) => {
    const activityId = (r.activity && r.activity.id) || null;
    if (!activityId || this.state.activityBusy) return;
    const headcount = Math.max(0, Math.trunc(Number(this.state.activityCount) || 0));
    this.setState({ activityBusy: true, activityError: null });
    try {
      await this.props.onStartActivity(activityId, { tableId, headcount });
      this.setState({ activityBusy: false, seatingActivity: null, activityCount: 0 });
    } catch (e) {
      this.setState({ activityBusy: false, activityError: e.message || 'No se pudo sentar la jornada' });
    }
  };

  /**
   * "Sentar a todos": en un evento que declara qué incluye la entrada ya está todo pago, así que
   * se da por llegado a todo el mundo. El backend sienta todas las mesas, reparte parejo a los
   * anotados y marca sus entradas como ingresadas; acá no se cuenta a nadie.
   */
  submitSeatEveryone = async (r) => {
    const activityId = (r.activity && r.activity.id) || null;
    if (!activityId || this.state.activityBusy) return;
    this.setState({ activityBusy: true, activityError: null });
    try {
      await this.props.onStartActivity(activityId, { everyone: true });
      this.setState({ activityBusy: false, seatingActivity: null, activityCount: 0 });
    } catch (e) {
      this.setState({ activityBusy: false, activityError: e.message || 'No se pudo sentar la jornada' });
    }
  };

  /**
   * FR-074 — panel de una mesa que tomó la jornada y que todavía no se sentó.
   *
   * Dos cosas que NO están y es a propósito: "Validar llegada" (el flujo genérico de sentar lo
   * rechaza el backend con 7307: la jornada se sienta entera, no mesa por mesa con un
   * `player_count` suelto) y "Cancelar / liberar" antes de la hora (la mesa se quita desde la
   * jornada, que es lo único que devuelve el cupo y actualiza el plan). "Liberar mesa" aparece
   * recién cuando la actividad arrancó, y el que lo decide es el backend: `hasStarted` viene
   * en el adjunto porque el navegador no puede derivar la jornada del salón (TC-002).
   */
  renderActivityPanel(table, r) {
    const act = r.activity || {};
    const { label: seatLabel } = this.activitySeat(r, table.id);
    const hermanas = this.activitySiblings(r, table.id);
    const name = act.name || r.name || 'Jornada del club';
    const started = !!act.hasStarted;
    const eventId = act.eventId || null;
    const irALaJornada = eventId ? () => navigateTo(`/editEvent?id=${eventId}`) : null;
    const seating = this.state.seatingActivity === r.id;
    // Evento con inclusiones: la acción principal es sentar a todos los anotados de una; el
    // formulario de siempre queda como alternativa. Sin anotados, sólo la alternativa.
    const conInclusiones = hasInclusions(includedOf(r));
    const anotados = Number(act.expected) || 0;
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 16 }}>{table.label}</div>
            <div style={{ fontSize: 12, color: STATE.actividad.text }}>
              Jornada · {pad(r.start_hour)}:00{seatLabel ? ` · ${seatLabel.toLowerCase()}` : ''}
            </div>
          </div>
          {/* Mover la mesa entera a otra libre SÍ se puede (FR-069): el backend conserva la
              jornada y re-apunta el plan. Es la salida cuando el salón necesita esa mesa. */}
          {this.renderMoveChip(r)}
        </div>
        {this.renderMove(r, table.id)}
        <div className="mt-3" />
        {this.row('Jornada', name)}
        {/* Anotados / ingresaron sólo tienen sentido con un evento detrás (en un torneo no hay
            entradas que redimir): sin él, no se inventan dos ceros. */}
        {act.ownerKind === 'event' && this.row('Anotados', act.expected)}
        {act.ownerKind === 'event' && this.row('Ingresaron', act.redeemed)}
        {hermanas.length > 0 && (
          <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
            <span className="text-muted">Otras mesas</span>
            <div className="mt-1">
              {hermanas.map((h) => (
                <span key={h.id} className="d-inline-block mr-1 mb-1" style={{
                  background: h.seated ? STATE.mesa.bg : STATE.actividad.bg,
                  color: h.seated ? STATE.mesa.text : STATE.actividad.text,
                  border: `1px solid ${h.seated ? STATE.mesa.border : STATE.actividad.border}`,
                  borderRadius: 10, padding: '1px 8px', fontSize: 12,
                }}>
                  {h.label}{h.seated ? ` · ${h.players}p` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {this.state.activityError && (
          <div className="alert alert-danger py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>{this.state.activityError}</div>
        )}

        {seating ? (
          <div className="mt-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
            <label className="d-block mb-1"><small>¿Cuánta gente hay en {table.label}?</small></label>
            <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
              <button type="button" className="btn btn-white btn-sm" disabled={(Number(this.state.activityCount) || 0) <= 0}
                onClick={() => this.stepActivitySeat(-1)}>–</button>
              <div style={{ minWidth: 32, textAlign: 'center', fontSize: 18, fontWeight: 500 }}>{Number(this.state.activityCount) || 0}</div>
              <button type="button" className="btn btn-white btn-sm" onClick={() => this.stepActivitySeat(1)}>+</button>
              <span className="text-muted ml-1" style={{ fontSize: 12 }}>dejalo en 0 para sólo sentar</span>
            </div>
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              Se sientan TODAS las mesas de {name}; la gente que cargues acá va a {table.label}.
              Ojo: cada entrada que el mostrador valide en Anotados suma +1 más, así que si ya
              están haciendo check-in, dejalo en 0.
            </p>
            <div className="d-flex" style={{ gap: 6 }}>
              <button type="button" className="btn btn-accent btn-sm flex-grow-1" disabled={this.state.activityBusy}
                onClick={() => this.submitStartActivity(r, table.id)}>Sentar</button>
              <button type="button" className="btn btn-white btn-sm flex-grow-1"
                onClick={() => this.setState({ seatingActivity: null, activityError: null })}>Cancelar</button>
            </div>
          </div>
        ) : conInclusiones ? (
          <React.Fragment>
            {anotados > 0 && (
              <button type="button" className="btn btn-accent btn-block mt-3" style={{ padding: '9px 10px' }}
                disabled={this.state.activityBusy} onClick={() => this.submitSeatEveryone(r)}>
                Sentar a todos ({anotados})
              </button>
            )}
            <button type="button" className={`btn btn-white btn-block ${anotados > 0 ? 'btn-sm mt-2' : 'mt-3'}`}
              onClick={() => this.setState({ seatingActivity: r.id, activityCount: 0, activityError: null })}>
              Sentar con otra cantidad
            </button>
          </React.Fragment>
        ) : (
          <button type="button" className="btn btn-accent btn-block mt-3" style={{ padding: '9px 10px' }}
            onClick={() => this.setState({ seatingActivity: r.id, activityCount: 0, activityError: null })}>
            Sentar la jornada
          </button>
        )}

        {irALaJornada && (
          <button type="button" className="btn btn-white btn-block btn-sm mt-2" onClick={irALaJornada}>
            Ir a la jornada
          </button>
        )}
        {started ? (
          <button type="button" className="btn btn-white btn-block btn-sm mt-2" onClick={() => this.props.onRelease(r.id)}>
            Liberar {table.label} (nadie se sentó): la mesa queda para el salón
          </button>
        ) : (
          <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
            Se quita desde la jornada{irALaJornada ? '' : ' (Eventos → la jornada → Mesas del salón)'}:
            así vuelve el cupo y el plan queda al día. Liberarla desde acá se habilita cuando la
            jornada arranca.
          </p>
        )}
        <button type="button" className="btn btn-link btn-block btn-sm mt-1 text-muted" style={{ fontSize: 12.5 }}
          onClick={() => this.props.onOpenDetail(r.id)}>Ver detalle</button>
      </div>
    );
  }

  renderOccupiedPanel(table, seated, upcoming) {
    // ENI-268: en una mesa gratis las entradas valen $0 (y el total de la reserva también).
    const coverFree = Number(seated.cover_free) === 1;
    const paidSeats = Number(seated.paid_seats) || 0;
    const totalSeats = Number(seated.player_count) || 0;
    const seatsPending = Math.max(0, totalSeats - paidSeats);
    // R-14: los importes salen del prorrateo de `total_amount` (lo que el backend cobra), no
    // de personas × cover vigente. Ver seatsChargeAmount en reservationsApi.
    const seatsPendingAmount = coverFree ? 0 : seatsChargeAmount(seated, paidSeats, totalSeats);
    const seatsTotalAmount   = coverFree ? 0 : seatsChargeAmount(seated, 0, totalSeats);
    // Saldo sin cobrar de la mesa: lugares pendientes + consumo impago (espeja
    // getOutstandingBalance del backend, que es quien realmente bloquea el liberado).
    const consumption = seated.consumption || { total: 0, pending: 0 };
    const debt = seatsPendingAmount + (Number(consumption.pending) || 0);
    const busy = this.state.seatsBusy;
    // FR-011/FR-050: la mesa abierta sentada sigue siendo una mesa abierta — no tiene titular
    // (name es null por diseño) y su dato es el M/N de anotados.
    const ot = seated.openTable || null;
    // FR-074: la hija sentada usa ESTE panel (stepper, consumos, "Tomar pedido", juegos,
    // "Liberar mesa"); lo que cambia es el encabezado y la fila Cliente, que pasa a ser la
    // jornada. Liberar está permitido: el backend acepta `completed` sobre una hija `seated`.
    const act = seated.activity || null;
    const actSeat = act ? this.activitySeat(seated, table.id) : null;
    // FR-084: la mesa de un evento que declara qué incluye muestra el contador del evento (el
    // mismo en todas sus mesas). Sin resumen es una mesa común (FR-122).
    const included = includedOf(seated);
    return (
      <div>
        {/* Encabezado: el nombre de la mesa y la acción que cambia el nombre de la mesa. */}
        <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 16 }}>{table.label}</div>
            <div style={{ fontSize: 12, color: ot ? STATE.abierta.text : (act ? STATE.actividad.text : STATE.mesa.text) }}>
              {ot ? 'Mesa abierta · en mesa desde ' : (act ? 'Jornada · en mesa desde ' : 'En mesa desde ')}{pad(seated.start_hour)}:00
              {actSeat && actSeat.label ? ` · ${actSeat.label.toLowerCase()}` : ''}
            </div>
          </div>
          {this.renderMoveChip(seated)}
        </div>
        {this.renderMove(seated, table.id)}
        {/* Hay una reserva más tarde sobre esta misma mesa: el mostrador tiene que saber
            a qué hora necesita liberarla. FR-050: la próxima puede ser una mesa abierta (sin
            titular), y decir "Reservada" hace pensar en una reserva común sin nombre. */}
        {upcoming && (
          <div className="alert alert-warning py-2 mt-2 mb-0" style={{ fontSize: 12 }}>
            {upcoming.activity
              // R-12: ESTE es el aviso que evita la mesa doble. Si a las 20 esta mesa sigue
              // ocupada, el check-in de la jornada no la puede sentar: deja la hija sin sentar
              // y avisa, y la noche arranca con una mesa menos.
              ? `Jornada ${upcoming.activity.name || upcoming.name || ''} a las ${pad(upcoming.start_hour)}:00 — la mesa tiene que quedar libre para esa hora.`
              : `${upcoming.openTable ? 'Mesa abierta' : 'Reservada'} a las ${pad(upcoming.start_hour)}:00${upcoming.name ? ` (${upcoming.name})` : ''}`}
          </div>
        )}

        <div className="mt-3">
          {/* La cantidad de personas se edita ACÁ, sobre el número, y no en un botón lejano.
              De a una: el caso real es "se fue uno" o "se sumó uno". */}
          <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid #eee' }}>
            <span className="text-muted">Personas</span>
            <span className="d-flex align-items-center" style={{ gap: 6 }}>
              <button type="button" className="btn btn-sm btn-white" title="Se fue una persona"
                style={{ lineHeight: 1, padding: '1px 8px' }}
                disabled={busy} onClick={() => this.stepSeats(seated, -1)}>−</button>
              <span style={{ fontWeight: 500, minWidth: 14, textAlign: 'center' }}>{totalSeats}</span>
              <button type="button" className="btn btn-sm btn-white" title="Se sumó una persona (la entrada va a la cuenta)"
                style={{ lineHeight: 1, padding: '1px 8px' }}
                disabled={busy} onClick={() => this.stepSeats(seated, 1)}>+</button>
              <span className="text-muted">/ {table.capacity}</span>
              {/* FR-063: la hija puede quedar en 0 y NO es una mesa que haya que liberar —
                  sigue siendo del club. Decirlo al lado del número, que es donde se mira. */}
              {act && totalSeats === 0 && (
                <span className="text-muted" style={{ fontSize: 12 }}>· mesa de {act.name || seated.name}</span>
              )}
            </span>
          </div>
          {this.state.seatsError && (
            <div className="alert alert-danger py-1 px-2 mt-2 mb-0" style={{ fontSize: 12 }}>{this.state.seatsError}</div>
          )}
          {/* FR-074: en una hija el "cliente" es la jornada — el `name` de la reserva es el
              nombre que el backend le copió, no una persona a la que llamar. */}
          {act ? this.row('Cliente', (
            <span>
              <span className="badge" style={{
                background: STATE.actividad.bg, color: STATE.actividad.text,
                border: `1px solid ${STATE.actividad.border}`,
              }}>Actividad · {act.name || seated.name}</span>
              {act.ownerKind === 'event' && (
                <small className="text-muted ml-2">{act.redeemed}/{act.expected} ingresaron</small>
              )}
            </span>
          )) : this.row('Cliente', ot
            ? (
              <span>
                <span className="badge badge-info">{RESERVATION_ORIGIN_LABEL.abierta}</span>
                <small className="text-muted ml-2">{ot.confirmed}/{ot.cupo} anotados</small>
              </span>
            )
            : (seated.name || '—'))}
          {this.renderMemberSeatRow(seated)}
          {this.renderTableVoucherRow(seated)}
          {this.row('Entradas', coverFree
            ? 'Gratis'
            : (() => {
              // Los socios sin cargo no son entradas: "2/3 pagas" habla sólo de los que pagan.
              const b = seatBreakdown(seated);
              // Con voucher de mesa no se dice "(pago)": esos lugares no se cobraron.
              const conVoucher = b.vouchers > 0 ? `${b.vouchers} con voucher` : '';
              if (b.paying === 0) return conVoucher || 'Sin cargo (socios)';
              if (seatsPending > 0) return `${b.paid}/${b.paying} pagas · faltan $${seatsPendingAmount}${conVoucher ? ` · ${conVoucher}` : ''}`;
              return conVoucher ? `${b.paid}/${b.paying} pagas · ${conVoucher}` : `$${seatsTotalAmount} (pago)`;
            })())}
          {this.row('Consumos', (() => {
            const c = seated.consumption || { total: 0, pending: 0 };
            if (!c.total) return '—';
            return c.pending > 0 ? `$${c.total} · $${c.pending} sin pagar` : `$${c.total} (pago)`;
          })())}
          {included && this.row('Incluido', (
            <span className="text-right d-inline-block">{this.renderIncludedCounter(included)}</span>
          ))}
          {included && this.renderIncludedOutside(included)}
          {included && Number(included.unpaidAdmissions) > 0 && (
            <div className="alert alert-warning py-2 mt-2 mb-0" style={{ fontSize: 12 }}>
              La entrada del evento está impaga: $ {Number(included.unpaidAmount || 0).toLocaleString('es-UY')} a cobrar en Eventos → Anotados
            </div>
          )}
          {this.renderGamesRow(seated)}
        </div>

        {/* El total, antes de los botones: hasta ahora había que sumar Entradas + Consumos de
            cabeza, y el número recién aparecía adentro del botón de liberar. */}
        <div className="d-flex justify-content-between align-items-baseline mt-2 pt-2" style={{ borderTop: '1px solid #e1e5eb' }}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>{debt > 0 ? 'Sin cobrar' : 'Cuenta saldada'}</span>
          <span style={{ fontSize: 18, fontWeight: 600 }}>${debt}</span>
        </div>

        {/* Una sola acción principal, que es lo que el mostrador hace nueve de cada diez veces.
            No se puede liberar con saldo sin cobrar (el backend lo rechaza), así que con deuda
            el botón lleva a cobrarla en vez de dejar que el usuario choque contra el error. */}
        {debt > 0 ? (
          <button type="button" className="btn btn-accent btn-block mt-2" style={{ padding: '9px 10px' }}
            onClick={() => this.openAccount(seated.id, { closing: true })}>
            Cobrar ${debt} y liberar
          </button>
        ) : (
          <button type="button" className="btn btn-accent btn-block mt-2" style={{ padding: '9px 10px' }}
            onClick={() => this.props.onRelease(seated.id)}>Liberar mesa</button>
        )}
        <div className="d-flex mt-2" style={{ gap: 8 }}>
          {/* Ir al POS a TOMAR un pedido para esta mesa. Deja la mesa preseleccionada. */}
          <button type="button" className="btn btn-white btn-sm flex-grow-1" onClick={() => this.props.onGoPos(seated.id)}>
            Tomar pedido
          </button>
          {/* El cobro parcial deja de ser la puerta principal y pasa a ser la excepción que es. */}
          <button type="button" className="btn btn-white btn-sm flex-grow-1" onClick={() => this.openAccount(seated.id)}>
            {debt > 0 ? 'Cobrar parte' : 'Ver cuenta'}
          </button>
        </div>
        {/* Sentada la mesa abierta, los anotados que faltan llegar se siguen validando por
            inscripción (FR-051): sin este acceso el camino era salir a Mesas abiertas. Los
            walk-ins de una mesa ya sentada entran por el stepper de Personas. */}
        {ot && (
          <button type="button" className="btn btn-white btn-block btn-sm mt-2"
            onClick={() => navigateTo(`/openTableSignups/${ot.id}`)}>Ver inscripciones</button>
        )}
        {/* Mismo criterio que la mesa abierta: la pantalla dueña queda a un click. Al cierre de
            la noche "Liberar todas" vive ahí, y es lo que hay que hacer con las N mesas. */}
        {act && act.eventId && (
          <button type="button" className="btn btn-white btn-block btn-sm mt-2"
            onClick={() => navigateTo(`/editEvent?id=${act.eventId}`)}>Ir a la jornada</button>
        )}
        <button type="button" className="btn btn-link btn-block btn-sm mt-1 text-muted" style={{ fontSize: 12.5 }}
          onClick={() => this.props.onOpenDetail(seated.id)}>Ver detalle</button>
      </div>
    );
  }

  // FR-050: panel de la mesa abierta antes de sentarse. La operación del día (check-in por
  // inscripción, no-show) vive en su pantalla de inscripciones: el primer check-in sienta la
  // reserva y la mesa pasa al flujo normal de 'mesa'.
  renderOpenTablePanel(table, r) {
    const ot = r.openTable;
    // Holds de pago online sin acreditar: ocupan cupo pero todavía no son gente confirmada.
    const pendientes = Math.max(0, Number(ot.taken) - Number(ot.confirmed));
    // FR-054: lugares que todavía se pueden vender en el mostrador. Los holds cuentan como
    // tomados (están pagando), así que el libre es contra `taken`, no contra `confirmed`.
    const libres = Math.max(0, Number(ot.cupo) - Number(ot.taken));
    const walkInOpen = this.state.checkingIn === r.id;
    return (
      <div>
        <div style={{ fontWeight: 500, fontSize: 16 }}>{table.label}</div>
        <div style={{ fontSize: 12, color: STATE.abierta.text, marginBottom: 12 }}>
          Mesa abierta · {pad(r.start_hour)}:00
        </div>
        {this.row('Anotados', `${ot.confirmed}/${ot.cupo}${pendientes > 0 ? ` (+${pendientes} pend. de pago)` : ''}`)}
        {/* spec-mesa-abierta FR-081: la gente llega diciendo "vengo a la mesa de Ana". */}
        {ot.hostName ? this.row('Abierta por', ot.hostName) : null}
        {this.row('Juego', r.game_name || '—')}
        {this.row('Cobro', CHARGE_MODE_LABEL[ot.chargeMode] || ot.chargeMode)}
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
          La llegada de los anotados se valida por inscripción: el primer check-in sienta la
          mesa y las entradas sin pagar quedan en su cuenta.
        </p>
        {/* FR-054: el que cae sin anotarse. Sin esto, una mesa abierta sin inscripciones no
            tenía forma de sentar a nadie desde el salón. */}
        {walkInOpen ? (
          <div className="mt-3 p-2" style={{ border: '1px solid #e1e5eb', borderRadius: 6 }}>
            <label className="d-block mb-1"><small>¿Cuántos se suman sin anotarse?</small></label>
            <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
              <button type="button" className="btn btn-white btn-sm" onClick={() => this.stepWalkIn(-1)}>–</button>
              <div style={{ minWidth: 32, textAlign: 'center', fontSize: 18, fontWeight: 500 }}>{this.state.checkInCount}</div>
              <button type="button" className="btn btn-white btn-sm" onClick={() => this.stepWalkIn(1)}>+</button>
              <span className="text-muted ml-1" style={{ fontSize: 12 }}>de {libres} lugar(es) libre(s)</span>
            </div>
            {(Number(this.state.checkInCount) || 0) > libres && (
              <div style={{ fontSize: 11, color: '#b5851a', marginBottom: 8 }}>
                Se pasa del cupo ({Number(ot.taken) + (Number(this.state.checkInCount) || 0)}/{ot.cupo}): entra igual.
              </div>
            )}
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              Se sientan en la mesa y sus entradas quedan pendientes en la cuenta: se cobran
              al liberarla o desde "Cobrar parte".
            </p>
            {this.state.checkInError && (
              <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{this.state.checkInError}</div>
            )}
            <div className="d-flex" style={{ gap: 6 }}>
              <button type="button" className="btn btn-accent btn-sm flex-grow-1" disabled={this.state.checkInBusy}
                onClick={() => this.submitWalkIn(r)}>Sentar</button>
              <button type="button" className="btn btn-white btn-sm flex-grow-1"
                onClick={() => this.setState({ checkingIn: null, checkInError: null })}>Cancelar</button>
            </div>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-accent btn-block mt-3" style={{ padding: '9px 10px' }}
              onClick={() => navigateTo(`/openTableSignups/${ot.id}`)}>
              Ver inscripciones
            </button>
            <button type="button" className="btn btn-white btn-block btn-sm mt-2"
              onClick={() => this.setState({ checkingIn: r.id, checkInCount: 1, checkInError: null })}>
              Sentar walk-in
            </button>
            {libres === 0 && (
              <p className="text-muted mt-1 mb-0" style={{ fontSize: 12 }}>
                El cupo está tomado ({ot.taken}/{ot.cupo}): el walk-in entra por encima.
              </p>
            )}
            {this.renderMemberWalkIn(r)}
          </>
        )}
        <button type="button" className="btn btn-link btn-block btn-sm mt-1 text-muted" style={{ fontSize: 12.5 }}
          onClick={() => this.props.onOpenDetail(r.id)}>Ver detalle</button>
      </div>
    );
  }

  renderReservedPanel(table, r) {
    // Entradas ya cobradas (fila "Entradas", abajo). En una reserva que todavía no llegó esto era
    // siempre 0 (el cover se cobra al llegar, ENI-222); desde que el pago por adelantado se puede
    // registrar —el caso de la transferencia previa— puede venir con la cuenta saldada, y el salón
    // tiene que decirlo antes de que alguien la cobre de nuevo.
    return (
      <div>
        {/* Mismo encabezado que la mesa ocupada: nombre + la acción que cambia el nombre.
            Mudarse antes de llegar (el cliente pidió otra mesa) es tan real como mudarse a
            mitad de la partida. */}
        <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 16 }}>{table.label}</div>
            <div style={{ fontSize: 12, color: STATE.reservada.text }}>
              Reservada {pad(r.start_hour)}:00 · {RESERVATION_STATUS[r.status] ? RESERVATION_STATUS[r.status].label : r.status}
            </div>
          </div>
          {this.renderMoveChip(r)}
        </div>
        {this.renderMove(r, table.id)}
        <div className="mt-3" />
        {this.row('Cliente', r.name || '—')}
        {/* El teléfono es el motivo por el que se abre este panel cuando el cliente no llega:
            estaba en la reserva pero no se mostraba en ningún lado del salón. */}
        {this.row('Teléfono', r.phone
          ? <a href={`tel:${String(r.phone).replace(/\s/g, '')}`}>{r.phone}</a>
          : <span className="text-muted">Sin teléfono</span>)}
        {this.row('Personas', r.player_count)}
        {this.row('Entradas', Number(r.cover_free) === 1
          ? 'Gratis'
          : (
            (() => {
              // Socios sin cargo: no suman al importe ni cuentan como cobradas (van dentro de paid_seats).
              const b = seatBreakdown(r);
              return (
                <span>
                  ${seatsChargeAmount(r, 0, b.players)}
                  {b.members > 0 && <small className="text-muted ml-1">(+{b.members} socio{b.members > 1 ? 's' : ''} sin cargo)</small>}
                  {b.paid > 0 && (
                    <span className={`badge ml-2 ${b.paid >= b.paying ? 'badge-success' : 'badge-warning'}`}>
                      {b.paid >= b.paying ? 'Paga' : `${b.paid}/${b.paying} cobradas`}
                    </span>
                  )}
                </span>
              );
            })()
          ))}
        {/* La entrada se cobra cuando el cliente llega (ENI-222), así que este es el momento
            —y el único sin plata de por medio— para corregir "reservó 5 y vinieron 4". */}
        {this.state.checkingIn === r.id ? this.renderArrivalForm(r) : (
          <button type="button" className="btn btn-accent btn-block mt-3" style={{ padding: '9px 10px' }}
            onClick={() => this.setState({ checkingIn: r.id, checkInCount: Number(r.player_count) || 1, checkInError: null })}>
            Validar llegada
          </button>
        )}
        <button type="button" className="btn btn-white btn-block btn-sm mt-2" onClick={() => this.props.onRelease(r.id)}>Cancelar / liberar</button>
        <button type="button" className="btn btn-link btn-block btn-sm mt-1 text-muted" style={{ fontSize: 12.5 }}
          onClick={() => this.props.onOpenDetail(r.id)}>Ver detalle</button>
      </div>
    );
  }

  render() {
    const { agendaLoaded } = this.props;
    const tables = (this.props.agenda.tables || [])
      .slice()
      .sort((a, b) => (a.capacity - b.capacity) || (a.is_vip - b.is_vip) || (a.id - b.id));

    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="m-0">Salón</h6>
          <span className="text-muted" style={{ fontSize: 12 }}>
            Actualiza solo
            <button type="button" className="btn btn-sm btn-white ml-2" onClick={this.props.onRefresh}>↻</button>
          </span>
        </div>

        {this.renderKpis(tables)}
        {this.renderLegend()}

        {!agendaLoaded && tables.length === 0 ? (
          <p className="text-muted">Cargando salón…</p>
        ) : (
          <div className="row">
            <div className="col-lg-8">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {tables.map((t) => this.renderCard(t))}
              </div>
            </div>
            <div className="col-lg-4 mt-3 mt-lg-0">
              <div className="card card-small">
                <div className="card-body" style={{ minHeight: 280 }}>
                  {this.renderPanel()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default SalonView;
